import { useState, useEffect, useRef } from "react";
import i18n from "../i18n";

// Guidance endpoints used to build category/keyword driven responses.
const GET_GUIDANCE_CATEGORIES_URL = "https://xianhefairy.com/LotusFairy_API/api/guidance/categories";
const GET_PRODUCTS_URL = "https://xianhefairy.com/LotusFairy_API/api/member/getProduct";
const GET_MASTERS_URL = "https://xianhefairy.com/LotusFairy_API/api/profile/GetAll";
// Question packs the user can top up with when the chat allowance runs low.
const LIST_CHAT_PRODUCTS_URL = "https://xianhefairy.com/LotusFairy_API/api/chat/ListChatProduct";
// Logs each asked question against the customer (this is what draws down their balance).
const SET_USER_CHAT_URL = "https://xianhefairy.com/LotusFairy_API/api/chat/SetUserChat";
// Buys question packs. The body is a list, so several packs could be bought in one call.
const PURCHASE_CHAT_URL = "https://xianhefairy.com/LotusFairy_API/api/chat/PurchaseChat";
// Remaining questions for the customer, and the gem balance a gem purchase is checked against.
const CHAT_CREDIT_URL = "https://xianhefairy.com/LotusFairy_API/api/chat/ChatCredit";
const GET_TOTAL_COINS_URL = "https://xianhefairy.com/LotusFairy_API/api/booking/getTotal";
const MASTER_BASE_URL = "http://localhost:14802";
const SHOP_SUGGEST_AFTER_EXCHANGE = 3; // suggest shop items after this many user messages
const CONSULTATION_SUGGEST_AFTER_EXCHANGE = 3; // suggest a master consultation after this many user messages
const HISTORY_TURNS_FOR_AI = 6; // prior chat messages passed to the model so short replies keep their thread
const HISTORY_CHARS_PER_TURN = 600; // cap per message so one long reply can't crowd out the prompt
const GET_GUIDANCE_KEYWORDS_URL = "https://xianhefairy.com/LotusFairy_API/api/guidance/keywords";
const GET_GUIDANCE_KEYWORDS_ALL_URL = "https://xianhefairy.com/LotusFairy_API/api/guidance/keywords/all";
const GET_GUIDANCE_ANSWER_URL = "https://xianhefairy.com/LotusFairy_API/api/guidance/answer";
// Crisis-intervention catalog (self-harm, domestic abuse, etc). Checked against every
// message before anything else — a hit short-circuits credit spend, translation and the
// AI call in favor of the scenario's own canned, per-language response.
const GET_SAFETY_SCENARIOS_URL = "https://xianhefairy.com/LotusFairy_API/api/guidance/safety-scenarios";
// Personalized opening greeting, keyed on the user's best and worst daily chart area.
const GET_GREETINGS_URL = "https://xianhefairy.com/LotusFairy_API/api/chatgreetings/GetGreetings";
// Temporarily point to local expanded-guidance endpoint for testing integration
// const GET_GUIDANCE_ANSWER_URL = "https://xianhefairy.com/LotusFairy_API/api/guidance/expanded-guidance";
// Optional direct-LLM path. If API key is available, this path bypasses guidance answer lookup.
const OPENROUTER_CHAT_URL = "https://openrouter.ai/api/v1/chat/completions";
const OPENROUTER_DEFAULT_MODEL = "google/gemini-3.1-flash-lite";
const OPENROUTER_DEFAULT_APP_NAME = "Lotus Fairy";
const OPENROUTER_RUNTIME_KEY_STORAGE = "OPENROUTER_API_KEY";
const OPENROUTER_PERIOD_CHOICES = ["daily", "monthly", "yearly", "natal"];
const AI_ONLY_OPENROUTER_MODE = true;

// Profile/forecast endpoints used to personalize guidance payloads.
const GET_PROFILE_URL = "https://xianhefairy.com/LotusFairy_API/api/User/GetProfile";
const GET_CHINESE_DOB_URL = "https://xianhefairy.com/LotusFairy_API/api/member/GetChineseDOB";
const GET_LUNAR_STEM_URL = "https://xianhefairy.com/LotusFairy_API/api/member/GetLunarHeavenlyStem";
const GET_LUNAR_STEMS_URL = "https://xianhefairy.com/LotusFairy_API/api/member/GetLunarHeavenlyStems";
const GET_NATAL_DAILY_FORECAST_URL = "https://xianhefairy.com/LotusFairy_API/api/member/GetNatalDailyForecast";
const API_TOKEN = "xAfhn90xflfHfd.fjldfj@fdhLXHCHchlhsdflsdfhawWFRwzxxp";
const PROFILE_CACHE_KEY = "fairy-chat-profile-cache-v3";
const PROFILE_CACHE_TTL_MS = 6 * 60 * 60 * 1000;
const KEYWORDS_CACHE_KEY = "fairy-chat-keywords-cache-v1";
const KEYWORDS_CACHE_TTL_MS = 6 * 60 * 60 * 1000;
// "General" keywords (e.g. wealth colour, favourable element) are answered from a fixed
// Gender/DayMaster/Strength lookup, not a period+chart one, so they're cached separately
// from the per-chart catalog above instead of living inside keywordsByChart.
const GENERAL_KEYWORDS_CACHE_KEY = "fairy-chat-general-keywords-cache-v1";
const GREETING_CACHE_KEY = "fairy-chat-greeting-cache-v1";
const SAFETY_SCENARIOS_CACHE_KEY = "fairy-chat-safety-scenarios-cache-v1";
const SAFETY_SCENARIOS_CACHE_TTL_MS = 6 * 60 * 60 * 1000;

const DEFAULT_PERIODS = ["daily", "monthly", "yearly", "natal"];
const DEFAULT_CHARTS = ["wealth", "health", "relationship", "career", "wisdom"];

// The UI language drives what the fairy answers in, and — because the keyword catalog and the
// guidance answer lookup are English-only — whether the question has to be translated first.
const UI_LANGUAGE_NAMES = {
  en: "English",
  id: "Bahasa Indonesia",
  cn: "Simplified Chinese",
};
const LANG_ID_TO_UI_CODE = {
  2224: "en",
  2225: "id",
  2226: "cn",
};

// Safety-scenarios rows carry one canned response per language, keyed on these field names.
const SAFETY_RESPONSE_FIELD_BY_UI_CODE = {
  en: "EN_Response",
  id: "ID_Response",
  cn: "ZH_Response",
};

// Per-language voice guidance layered onto the AI prompts on top of the base persona.
// Each entry tunes the check-in cadence, local flavor, and — critically — the length
// cap for that language, since word/character targets don't translate 1:1 across
// English, Chinese density, and Indonesian. Keyed on the UI code so `getUiLanguage()` /
// LANG_ID_TO_UI_CODE feed it directly.
const LANGUAGE_STYLE_GUIDANCE = {
  en: "Keep the check-in habit (\"okay?\"/\"you know?\"/\"right?\") restrained: at most one per response, never stacked, and roughly half your responses should have none at all. When you do use one, vary where it lands — mid-thought reads differently from a trailing tag at the very end. If every response ends the same way, it stops sounding like her and starts sounding like a script. Do NOT use Chinese words or particles here (对不对/是不是/嘛 etc. belong to the Chinese output only) — instead, a light Singlish flavor carries the local voice: natural fillers and sentence patterns like \"can,\" \"one,\" \"already,\" \"lah,\" used sparingly, not on every line. Length: aim for 50-75 words, hard cap 95 — for the whole response, every paragraph added together, not per paragraph.",
  cn: "Write in colloquial Singapore Mandarin (Singdarin), not mainland textbook Putonghua — use natural local particles like 啦/咯/meh where they fit the tone, and don't hesitate to mix in the occasional English word the way Singaporeans actually speak. Avoid stiff mainland-standard idioms. Master is English-educated, not a classically-trained CN speaker — her Chinese vocabulary is everyday and conversational, not literary. Avoid 成语 (four-character idioms, even common/mild-sounding ones — e.g. 囊中物, 稳扎稳打) and bookish/classical phrasing generally — she wouldn't reach for these naturally. When a concept would normally call for an idiom, say it plainly in simple words instead, or switch to the English term/phrase. \"Chart\" is off-limits here too — say 你的解读 (\"your interpretation\") instead of 你的chart, and avoid 你的盘/你的命盘 as well, since those are the same structural jargon in Chinese. For other genuinely untranslatable ideas (non-jargon ones), it's still fine to keep the occasional term as an English loanword rather than reaching for a stiff, formal/technical CN equivalent. Also keep the check-in habit (对不对/是不是/嘛/right?) restrained: at most one per response, and roughly half your responses should have none at all. Don't let 啦/咯/meh replace that habit entirely or drop it entirely; the two particle sets serve different jobs (啦/咯/meh = local flavor, 对不对/是不是/嘛 = her check-in cadence) and both should show up across a conversation, just not stacked in the same line. Length: aim for 80-120 characters (not English word-count, which doesn't map cleanly to Chinese density), hard cap 150 characters — for the whole response, every paragraph added together, not per paragraph.",
  id: "Write like a kakak bijak (a warm, wise older sibling) talking to a younger sibling — casual 'kamu' register, but lead with warmth and validation before advice, not a stern warning. Soften cautions with empathy first (e.g. 'aku ngerti kok kalau...') instead of opening with 'jangan' or 'dengar ya'. When you do check in, do it gently ('ya?', 'kan?') rather than 'mengerti?', which reads as scolding — but keep it restrained: at most one check-in per response, and roughly half your responses should end with no check-in at all. A 'ya?'/'kan?' on every single line reads as a verbal tic, not warmth. Length: aim for 50-75 words, hard cap 95 — for the whole response, every paragraph added together, not per paragraph.",
};

const getLanguageStyleGuidance = (language) => LANGUAGE_STYLE_GUIDANCE[language?.code] || "";

// Matches TopBar's locale handling ("en-US" -> "en", any "zh*" -> "cn").
const normalizeUiLocale = (value = "") => {
  const code = String(value || "").split(/[-_]/)[0].trim().toLowerCase();
  return code.startsWith("zh") ? "cn" : code;
};

// Read the language the client is actually *reading*, which is whatever i18n is rendering
// with — not localStorage. The two drift: `syncProfileLanguageFromGetProfile` rewrites
// localStorage.language from the server profile's LangId on load, so a topbar switch whose
// server-side save has not landed yet gets reset to "en" while i18n (and therefore every
// visible string) stays on the picked language. Reading localStorage first made the fairy
// answer in English on an otherwise fully Indonesian page.
const getUiLanguage = () => {
  let code = "";

  try {
    code = normalizeUiLocale(i18n?.language);

    if (!UI_LANGUAGE_NAMES[code]) {
      code = normalizeUiLocale(sessionStorage.getItem("topBarLangOverride"));
    }
    if (!UI_LANGUAGE_NAMES[code]) {
      code = normalizeUiLocale(localStorage.getItem("language"));
    }
    if (!UI_LANGUAGE_NAMES[code]) {
      const langId = parseInt(localStorage.getItem("languageId") || "", 10);
      code = LANG_ID_TO_UI_CODE[langId] || "";
    }
  } catch {
    code = "";
  }

  const normalized = UI_LANGUAGE_NAMES[code] ? code : "en";
  return { code: normalized, name: UI_LANGUAGE_NAMES[normalized] };
};

const hasRequiredProfileFields = (profile) => (
  !!profile?.gender
  && !!profile?.strengthCategory
  && !!profile?.lunarData?.A2
);

const readCachedProfile = (userId) => {
  try {
    const raw = localStorage.getItem(PROFILE_CACHE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || parsed.userId !== userId) return null;
    if (!parsed.expiresAt || parsed.expiresAt < Date.now()) return null;

    return parsed.profile || null;
  } catch {
    return null;
  }
};

const writeCachedProfile = (userId, profile) => {
  try {
    localStorage.setItem(
      PROFILE_CACHE_KEY,
      JSON.stringify({
        userId,
        expiresAt: Date.now() + PROFILE_CACHE_TTL_MS,
        profile,
      })
    );
  } catch {
    // Ignore storage failures.
  }
};

// Singapore calendar day (YYYY-MM-DD). SG has no DST, so a fixed +8h shift is exact and the
// day rolls over at 12am SGT regardless of the device's own timezone.
const getSingaporeDayKey = () => new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString().slice(0, 10);

// The opening greeting is generated once per user, language and SG day, so a chat reset
// shows the same text instead of asking the API and the blend model for a new one.
const readCachedGreeting = (userId, langCode) => {
  try {
    const raw = localStorage.getItem(GREETING_CACHE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || parsed.userId !== userId || parsed.langCode !== langCode) return null;
    if (parsed.day !== getSingaporeDayKey()) return null;

    return parsed.text || null;
  } catch {
    return null;
  }
};

const writeCachedGreeting = (userId, langCode, text) => {
  try {
    localStorage.setItem(
      GREETING_CACHE_KEY,
      JSON.stringify({ userId, langCode, day: getSingaporeDayKey(), text })
    );
  } catch {
    // Ignore storage failures.
  }
};

const toChartNumber = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeChartValues = (row) => {
  if (!row) return null;
  return {
    WEALTH: toChartNumber(row.WEALTH_TOTAL ?? row.WEALTH),
    CAREER: toChartNumber(row.CAREER_TOTAL ?? row.CAREER),
    RS: toChartNumber(row.RS_TOTAL ?? row.RS),
    HEALTH: toChartNumber(row.HEALTH_TOTAL ?? row.HEALTH),
    WISDOM: toChartNumber(row.WISDOM_TOTAL ?? row.WISDOM),
  };
};

const hasForecastTables = (data) => (
  !!data
  && Array.isArray(data.Table)
  && data.Table.length > 0
  && Array.isArray(data.Table1)
  && data.Table1.length > 0
  && Array.isArray(data.Table2)
  && data.Table2.length > 0
);

const parseDobLikeNatalChart = (rawDob) => {
  let parsed = new Date(rawDob);
  if (!isNaN(parsed.getTime())) return parsed;

  parsed = new Date(String(rawDob).replace(/-/g, "/"));
  if (!isNaN(parsed.getTime())) return parsed;

  const parts = String(rawDob).split(/[-/]/);
  if (parts.length === 3) {
    parsed = new Date(`${parts[2]}/${parts[1]}/${parts[0]}`);
    if (!isNaN(parsed.getTime())) return parsed;
  }

  return null;
};

const normalizeForMatch = (text = "") => text.toLowerCase().replace(/\s+/g, " ").trim();

// The product API spells this field differently per environment: production sends
// LIFE_AREAS, the test server sends LIFE_ARES (sic) alongside LIST_LIFE_AREAS, an
// array of { LifeAreas } carrying the item's full multi-select. Reading only one
// spelling leaves every product with no life area on the other backend, which drops
// the entire shop strip without an error — the masters still appear, so the panel
// just looks like it has nothing to sell.
const readProductLifeAreas = (item) => {
  const values = [];

  const pushCsv = (raw) => {
    if (typeof raw !== "string" && typeof raw !== "number") return;
    String(raw)
      .split(",")
      .forEach((part) => values.push(part.trim().toLowerCase()));
  };

  pushCsv(item?.LIFE_AREAS);
  pushCsv(item?.LIFE_ARES);

  if (Array.isArray(item?.LIST_LIFE_AREAS)) {
    item.LIST_LIFE_AREAS.forEach((entry) => pushCsv(entry?.LifeAreas ?? entry));
  }

  // "0" is the API's stand-in for "unassigned"; it must not become a match term.
  return Array.from(new Set(values.filter((value) => value && value !== "0")));
};

const getLifeAreaMatchTerms = (chart) => {
  if (!chart) return [];
  const lower = normalizeChartName(chart);
  if (lower === "relationship") return ["relationship", "rs", "love"];
  return [lower];
};

const extractPeriodFromDescEng = (descEng = "") => {
  const lower = String(descEng || "").toLowerCase();
  if (lower.includes("daily")) return "daily";
  if (lower.includes("monthly")) return "monthly";
  if (lower.includes("yearly") || lower.includes("annual")) return "yearly";
  if (lower.includes("natal")) return "natal";
  return null;
};

// ListChatProduct returns rows like { Id, DescEng: "10 Question", Cash, Gem }.
// The question count only exists inside DescEng, so pull it out for the UI.
const normalizeChatProduct = (item = {}) => {
  const label = String(item.DescEng || "").trim();
  const countMatch = label.match(/\d[\d,]*/);
  return {
    id: item.Id,
    label,
    questions: countMatch ? Number(countMatch[0].replace(/,/g, "")) : null,
    cash: Number(item.Cash) || 0,
    gem: Number(item.Gem) || 0,
    raw: item,
  };
};

const getCustId = () => parseInt(localStorage.getItem("userID") || "", 10) || 0;

// Questions the customer still has. The endpoint answers a bare object, but tolerate a
// single-row list too. Returns null when the figure can't be read, so the UI can stay quiet.
const fetchChatCredit = async () => {
  const custId = getCustId();
  if (!custId) {
    console.warn("[useFairyChat] ChatCredit skipped: no userID in localStorage");
    return null;
  }

  try {
    // console.log("[useFairyChat] ChatCredit request:", { CustId: custId });
    const res = await fetch(`${CHAT_CREDIT_URL}?CustId=${custId}`);
    const data = await res.json();
    // console.log("[useFairyChat] ChatCredit response:", data);

    const row = Array.isArray(data) ? data[0] : data;
    if (!row || row.ChatCredit == null) {
      console.warn("[useFairyChat] ChatCredit missing from response, leaving count unset");
      return null;
    }

    const credit = Number(row.ChatCredit) || 0;
    // console.log("[useFairyChat] Chat credit remaining:", credit);
    return credit;
  } catch (error) {
    console.error("[useFairyChat] ChatCredit failed:", error);
    return null;
  }
};

// Gem balance, read from the member row the booking endpoint returns.
const fetchGemBalance = async () => {
  const custId = getCustId();
  if (!custId) {
    console.warn("[useFairyChat] getTotal skipped: no userID in localStorage");
    return 0;
  }

  try {
    // console.log("[useFairyChat] getTotal request:", { ID: custId });
    const res = await fetch(`${GET_TOTAL_COINS_URL}?ID=${custId}`);
    const data = await res.json();
    if (!Array.isArray(data) || data.length === 0) {
      console.warn("[useFairyChat] getTotal returned no member row:", data);
      return 0;
    }

    const gems = Number(data[0].COINS) || 0;
    // console.log("[useFairyChat] Gem balance:", gems);
    return gems;
  } catch (error) {
    console.error("[useFairyChat] getTotal failed:", error);
    return 0;
  }
};

const normalizeChartName = (value = "") => {
  const lower = String(value).trim().toLowerCase();
  if (lower === "rs") return "relationship";
  return lower;
};

const getChartSynonyms = (chart) => {
  if (chart === "relationship") return ["relationship", "rs"];
  return [chart];
};

const getExplicitPeriod = (text = "") => {
  const lower = text.toLowerCase();
  if (/\b(daily|today|tonight)\b/.test(lower) || lower.includes("right now")) return "daily";
  if (/\b(monthly|this month)\b/.test(lower)) return "monthly";
  if (/\b(yearly|annual|this year)\b/.test(lower) || lower.includes("year ")) return "yearly";
  if (/\bnatal\b/.test(lower)) return "natal";
  return null;
};

const extractChartFromText = (text = "", charts = DEFAULT_CHARTS) => {
  const lower = normalizeForMatch(text);
  for (const chart of charts) {
    const variants = getChartSynonyms(chart);
    for (const variant of variants) {
      const regex = new RegExp(`\\b${variant}\\b`, "i");
      if (regex.test(lower)) return chart;
    }
  }
  return null;
};

const dedupeKeywords = (values = []) => {
  const seen = new Set();
  const unique = [];

  values.forEach((value) => {
    const normalized = normalizeForMatch(String(value || ""));
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    unique.push(normalized);
  });

  return unique;
};

const normalizeKeywordsPayload = (payload, charts = DEFAULT_CHARTS) => {
  const source = payload && typeof payload === "object"
    ? (payload.Keywords && typeof payload.Keywords === "object" ? payload.Keywords : payload)
    : null;
  if (!source) return {};

  const allowedCharts = new Set((charts || DEFAULT_CHARTS).map((chart) => normalizeChartName(chart)));
  const normalized = {};

  Object.entries(source).forEach(([rawChart, rawKeywords]) => {
    const chart = normalizeChartName(rawChart);
    if (!chart || !allowedCharts.has(chart) || !Array.isArray(rawKeywords)) return;
    normalized[chart] = dedupeKeywords(rawKeywords);
  });

  return normalized;
};

const mergeKeywordMaps = (baseMap = {}, incomingMap = {}, charts = DEFAULT_CHARTS) => {
  const result = { ...baseMap };
  const chartList = Array.from(new Set((charts || DEFAULT_CHARTS).map((chart) => normalizeChartName(chart))));

  chartList.forEach((chart) => {
    const merged = dedupeKeywords([...(baseMap[chart] || []), ...(incomingMap[chart] || [])]);
    if (merged.length > 0) {
      result[chart] = merged;
    }
  });

  return result;
};

// Pulls the "general" bucket out of the same /keywords/all payload the per-chart catalog
// comes from, independent of normalizeKeywordsPayload's chart allow-list so it isn't
// dropped as an unrecognized chart.
const extractGeneralKeywordsPayload = (payload) => {
  const source = payload && typeof payload === "object"
    ? (payload.Keywords && typeof payload.Keywords === "object" ? payload.Keywords : payload)
    : null;
  const raw = source?.general ?? source?.General;
  return Array.isArray(raw) ? dedupeKeywords(raw) : [];
};

const readCachedGeneralKeywords = () => {
  try {
    const raw = localStorage.getItem(GENERAL_KEYWORDS_CACHE_KEY);
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.expiresAt || parsed.expiresAt < Date.now()) return [];

    return Array.isArray(parsed.keywords) ? parsed.keywords : [];
  } catch {
    return [];
  }
};

const writeCachedGeneralKeywords = (keywords = []) => {
  try {
    if (!Array.isArray(keywords) || keywords.length === 0) return;

    localStorage.setItem(
      GENERAL_KEYWORDS_CACHE_KEY,
      JSON.stringify({
        expiresAt: Date.now() + KEYWORDS_CACHE_TTL_MS,
        keywords: dedupeKeywords(keywords),
      })
    );
  } catch {
    // Ignore storage failures.
  }
};

const readCachedKeywords = (charts = DEFAULT_CHARTS) => {
  try {
    const raw = localStorage.getItem(KEYWORDS_CACHE_KEY);
    if (!raw) return {};

    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.expiresAt || parsed.expiresAt < Date.now()) return {};

    return normalizeKeywordsPayload(parsed.keywordsByChart, charts);
  } catch {
    return {};
  }
};

const writeCachedKeywords = (keywordMap, charts = DEFAULT_CHARTS) => {
  try {
    const normalized = normalizeKeywordsPayload(keywordMap, charts);
    const mergedWithExisting = mergeKeywordMaps(readCachedKeywords(charts), normalized, charts);
    const hasAnyKeywords = Object.values(mergedWithExisting).some((values) => Array.isArray(values) && values.length > 0);
    if (!hasAnyKeywords) return;

    localStorage.setItem(
      KEYWORDS_CACHE_KEY,
      JSON.stringify({
        expiresAt: Date.now() + KEYWORDS_CACHE_TTL_MS,
        keywordsByChart: mergedWithExisting,
      })
    );
  } catch {
    // Ignore storage failures.
  }
};

const escapeRegex = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const hasNonAsciiChars = (text = "") => [...text].some((char) => char.charCodeAt(0) > 127);

// Containment check used only by the near-miss fallback below. CJK has no word separators, so
// raw substring containment is the only option there. ASCII has to respect word boundaries:
// plain `includes` lets a common prefix like "health" swallow "healthcare", which pulled career
// questions about the healthcare industry into the health chart. A trailing plural is still
// allowed, since "new jobs" and "new job" are the near-miss this fallback exists to catch.
const containsLoosely = (haystack, needle) => {
  if (!haystack || !needle) return false;
  if (hasNonAsciiChars(haystack) || hasNonAsciiChars(needle)) return haystack.includes(needle);

  return new RegExp(`(^|[^a-z0-9])${escapeRegex(needle)}(e?s)?($|[^a-z0-9])`, "i").test(haystack);
};

// Catches a catalog keyword stored as one compound word ("hairstyle") against a user who
// naturally splits it into two ("hair style"), or vice versa. Strips whitespace from both
// sides and falls back to plain substring containment, gated by length so a short keyword
// ("ex", "4d") can't fire off an unrelated run of words that happens to squish together.
const textMatchesKeywordLoosely = (normalizedText, keyword) => {
  const squishedKeyword = keyword.replace(/\s+/g, "");
  if (squishedKeyword.length < 6) return false;

  const squishedText = normalizedText.replace(/\s+/g, "");
  return squishedText.includes(squishedKeyword);
};

const textMatchesKeyword = (normalizedText, keyword) => {
  if (!keyword) return false;
  if (normalizedText === keyword) return true;

  // CJK text has no whitespace between words, so the word-boundary rule below can never
  // fire for it. Fall back to containment, with a length guard so a single character
  // does not match half the catalog.
  if (hasNonAsciiChars(keyword)) {
    return keyword.length >= 2 && normalizedText.includes(keyword);
  }

  const regex = new RegExp(`(^|\\s)${escapeRegex(keyword)}(?=$|\\s|[^a-zA-Z0-9])`, "i");
  if (regex.test(normalizedText)) return true;

  return textMatchesKeywordLoosely(normalizedText, keyword);
};

// Catalog synonyms often lead with a qualifier word ("suitable hairstyle", "favourable
// color", "improve relationship") that a real question rarely repeats verbatim — "what
// hair style suits me" carries the topic, not the word "suitable". Stripping these before
// matching lets the topic word itself stand as its own candidate, without loosening
// matching on the topic word itself.
const KEYWORD_FILLER_WORDS = new Set(["suitable", "favourable", "favorable", "improve", "boost", "my", "good"]);

const stripKeywordFillerWords = (phrase) => phrase
  .split(" ")
  .filter((word) => word && !KEYWORD_FILLER_WORDS.has(word))
  .join(" ");

// Keyword rows may bundle synonyms into one comma-separated string (e.g.
// "4d, 买彩票, 中彩, 彩票"). The guidance answer API matches on the whole bundle, so match
// the user's text against each synonym but always report the full bundle back, exactly
// like the keyword chips do. Returns the most specific match across the list.
const findKeywordMatch = (text, chartKeywords = []) => {
  const normalizedText = normalizeForMatch(text);
  if (!normalizedText) return null;

  let best = null;
  for (const bundle of chartKeywords) {
    const parts = bundle
      .split(",")
      .map((part) => normalizeForMatch(part))
      .filter(Boolean);
    if (parts.length === 0) continue;

    // The full bundle is itself a candidate: keyword chips send it verbatim.
    const baseCandidates = parts.length > 1 ? [normalizeForMatch(bundle), ...parts] : parts;
    const candidates = new Set(baseCandidates);
    baseCandidates.forEach((candidate) => {
      const stripped = stripKeywordFillerWords(candidate);
      if (stripped && stripped !== candidate) candidates.add(stripped);
    });

    for (const candidate of candidates) {
      if (!textMatchesKeyword(normalizedText, candidate)) continue;
      const score = getKeywordSpecificityScore(candidate);
      if (!best || score > best.score) best = { bundle, matched: candidate, score };
    }
  }

  return best;
};

const findKeywordInText = (text, chartKeywords = []) => findKeywordMatch(text, chartKeywords)?.bundle || null;

// Most safety-scenarios rows bundle keywords across every language directly in one flat
// list (e.g. self_harm carries "kill myself", "自杀", "bunuh diri" together) — any single
// hit should trigger them. A few rows (csam, csam_report) instead carry the *source* of two
// Python list literals (MINOR_TERMS = [...], SEXUAL_TERMS = [...]) that got comma-split into
// that same flat array without stripping the variable-name/bracket boilerplate. Treating
// those as one flat OR-list would fire the CSAM refusal on the word "child" alone — the
// MINOR_TERMS group is only meant to matter together with a SEXUAL_TERMS hit. The "NAME = ["
// markers are still sitting in the raw strings, so they're used below to recover the
// original groups; a scenario matches only when every recovered group has a hit (AND across
// groups, OR within one) — which degrades to plain flat-list OR matching for rows with no
// markers, exactly what self_harm/domestic_abuse need.
const SAFETY_KEYWORD_GROUP_MARKER = /[A-Z][A-Z0-9_]*\s*=\s*\[/;

const cleanSafetyKeywordToken = (raw = "") => (
  String(raw)
    .replace(/[[\]]/g, "")
    .trim()
    // Only strip quotes acting as delimiters around the whole token (leftover from the
    // Python-list-literal source rows) — not apostrophes inside a word, which would mangle
    // contractions like "can't breathe" into "cant breathe" and break matching against the
    // user's actual (apostrophe-intact) text.
    .replace(/^["']+|["']+$/g, "")
    .replace(/\s+/g, " ")
    .trim()
);

const parseSafetyKeywordGroups = (rawKeywords = []) => {
  const groups = [];
  let current = [];

  const pushCurrent = () => {
    const cleaned = current.map(cleanSafetyKeywordToken).filter(Boolean);
    if (cleaned.length > 0) groups.push(cleaned);
    current = [];
  };

  rawKeywords.forEach((rawToken) => {
    const text = String(rawToken || "");
    const marker = text.match(SAFETY_KEYWORD_GROUP_MARKER);
    if (marker) {
      // Whatever sits before the marker (e.g. a stray "]" closing the previous group) is
      // boilerplate, not a keyword; whatever follows it is the first token of a new group.
      pushCurrent();
      current.push(text.slice(marker.index + marker[0].length));
    } else {
      current.push(text);
    }
  });
  pushCurrent();

  return groups;
};

const normalizeSafetyScenario = (row = {}) => {
  const scenario = String(row.Scenario || row.scenario || "").trim();
  const rawKeywords = Array.isArray(row.Keywords) ? row.Keywords : Array.isArray(row.keywords) ? row.keywords : [];
  const keywordGroups = parseSafetyKeywordGroups(rawKeywords);
  if (!scenario || keywordGroups.length === 0) return null;

  return {
    scenario,
    keywordGroups,
    EN_Response: String(row.EN_Response || row.en_response || "").trim(),
    ZH_Response: String(row.ZH_Response || row.zh_response || "").trim(),
    ID_Response: String(row.ID_Response || row.id_response || "").trim(),
  };
};

export const normalizeSafetyScenariosPayload = (data) => (
  Array.isArray(data) ? data.map(normalizeSafetyScenario).filter(Boolean) : []
);

// Safety-scenario matching tolerates two things the generic chart-keyword matcher doesn't:
// contractions typed without an apostrophe ("cant breathe" vs the stored "can't breathe"),
// and real phrasing that inserts a few words between/around the keyword's own words ("i cant
// really breathe right now"). Punctuation is stripped from both sides before comparing so
// apostrophe variants collapse together; other punctuation becomes a space so words don't
// fuse ("don't-know" -> "dont know", not "dontknow").
const stripSafetyMatchPunctuation = (text = "") => (
  text
    .replace(/[’‘´`']/g, "")
    .replace(/[^a-z0-9\s]/gi, " ")
);

// How many extra words are allowed between/around the keyword's own words before a text is
// no longer considered a match. Keeps "i cant really breathe right now" matching "can't
// breathe" while stopping an unrelated long message that merely contains the same two words
// scattered far apart from falsely triggering a crisis response.
const SAFETY_MATCH_WORD_SLOP = 3;

// Standard "minimum window containing every required word" two-pointer scan: slides a window
// over textTokens and returns the shortest span (in token count) that contains at least one
// occurrence of every word in requiredWords, in any order. Infinity if no such window exists.
const findMinimalTokenSpan = (textTokens, requiredWords) => {
  const counts = new Map(requiredWords.map((word) => [word, 0]));
  let have = 0;
  let left = 0;
  let bestSpan = Infinity;

  for (let right = 0; right < textTokens.length; right += 1) {
    const word = textTokens[right];
    if (!counts.has(word)) continue;
    counts.set(word, counts.get(word) + 1);
    if (counts.get(word) === 1) have += 1;

    while (have === requiredWords.length) {
      bestSpan = Math.min(bestSpan, right - left + 1);
      const leftWord = textTokens[left];
      if (counts.has(leftWord)) {
        counts.set(leftWord, counts.get(leftWord) - 1);
        if (counts.get(leftWord) === 0) have -= 1;
      }
      left += 1;
    }
  }

  return bestSpan;
};

const textSatisfiesSafetyKeyword = (normalizedText, keyword) => {
  if (!keyword) return false;

  // CJK has no word separators, so it keeps the plain containment check used elsewhere.
  if (hasNonAsciiChars(keyword)) {
    return keyword.length >= 2 && normalizedText.includes(keyword);
  }

  const cleanedText = stripSafetyMatchPunctuation(normalizedText).replace(/\s+/g, " ").trim();
  const cleanedKeyword = stripSafetyMatchPunctuation(keyword).replace(/\s+/g, " ").trim();
  const keywordTokens = cleanedKeyword.split(" ").filter(Boolean);
  if (keywordTokens.length === 0) return false;

  // Single-word keywords keep the original strict word-boundary match — no span math needed —
  // plus the same squish-and-contain fallback used for topic keywords, so a crisis word typed
  // with an unexpected space ("over dose") still fires. This is safety-critical matching: it
  // only ever makes the check MORE sensitive, never less, so it can't cause a real match to be
  // missed that the strict check alone would have caught.
  if (keywordTokens.length === 1) {
    const regex = new RegExp(`(^|\\s)${escapeRegex(keywordTokens[0])}(?=$|\\s|[^a-zA-Z0-9])`, "i");
    if (regex.test(cleanedText)) return true;
    return textMatchesKeywordLoosely(cleanedText, keywordTokens[0]);
  }

  const textTokens = cleanedText.split(" ").filter(Boolean);
  const requiredWords = [...new Set(keywordTokens)];
  const span = findMinimalTokenSpan(textTokens, requiredWords);
  return span <= requiredWords.length + SAFETY_MATCH_WORD_SLOP;
};

// First matching scenario wins — these are crisis triggers, not topic keywords, so there's
// no need to rank matches by specificity the way chart keywords are.
export const findSafetyScenarioMatch = (text, scenarios = []) => {
  const normalizedText = normalizeForMatch(text);
  if (!normalizedText) return null;

  return scenarios.find((scenario) => (
    scenario.keywordGroups.every((group) => (
      group.some((keyword) => textSatisfiesSafetyKeyword(normalizedText, normalizeForMatch(keyword)))
    ))
  )) || null;
};

// Falls back to the English response whenever the languageId is unrecognized or that
// scenario's own translation is missing, so a crisis message is never shown blank.
const getSafetyScenarioResponseText = (scenario, langId) => {
  const uiCode = LANG_ID_TO_UI_CODE[langId] || "en";
  const field = SAFETY_RESPONSE_FIELD_BY_UI_CODE[uiCode] || "EN_Response";
  return scenario?.[field] || scenario?.EN_Response || "";
};

const readCachedSafetyScenarios = () => {
  try {
    const raw = localStorage.getItem(SAFETY_SCENARIOS_CACHE_KEY);
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.expiresAt || parsed.expiresAt < Date.now()) return [];

    return Array.isArray(parsed.scenarios) ? parsed.scenarios : [];
  } catch {
    return [];
  }
};

const writeCachedSafetyScenarios = (scenarios = []) => {
  try {
    if (!Array.isArray(scenarios) || scenarios.length === 0) return;

    localStorage.setItem(
      SAFETY_SCENARIOS_CACHE_KEY,
      JSON.stringify({
        expiresAt: Date.now() + SAFETY_SCENARIOS_CACHE_TTL_MS,
        scenarios,
      })
    );
  } catch {
    // Ignore storage failures.
  }
};

// The guidance answer API matches `keyword` against the exact comma-bundled synonym
// string stored on the row (e.g. "4d, 买彩票, 中彩, 彩票"), so posting a single detected
// synonym returns 404. Map a loose keyword back to the bundle that contains it, and
// report which chart that bundle belongs to so the category stays consistent with it.
const resolveKeywordBundle = (keyword, keywordsByChart = {}, preferredCharts = []) => {
  const target = normalizeForMatch(keyword);
  if (!target) return null;

  const preferred = preferredCharts.filter(Boolean).map((chart) => normalizeChartName(chart));
  const chartOrder = [
    ...preferred,
    ...Object.keys(keywordsByChart).filter((chart) => !preferred.includes(chart)),
  ];

  let looseMatch = null;
  for (const chart of chartOrder) {
    for (const bundle of keywordsByChart[chart] || []) {
      const parts = bundle
        .split(",")
        .map((part) => normalizeForMatch(part))
        .filter(Boolean);
      if (parts.length === 0) continue;

      if (normalizeForMatch(bundle) === target || parts.includes(target)) {
        return { bundle, chart };
      }
      // Fallback for near-misses (AI says "彩票" where the row stores "买彩票", or vice
      // versa). Minimum lengths keep short entries like "ex" or "4d" from matching any
      // longer word that happens to contain them, and `containsLoosely` keeps an English
      // synonym from matching a longer word it is merely a prefix of.
      if (!looseMatch) {
        const partial = parts.some((part) => (
          (target.length >= 2 && containsLoosely(part, target))
          || (part.length >= 3 && containsLoosely(target, part))
        ));
        if (partial) looseMatch = { bundle, chart };
      }
    }
  }

  return looseMatch;
};

// Interrogative and modal phrases sit in the wisdom catalog but carry no topic of their own.
// The score below rewards word count, so a two-word phrase like "can i" outranks a real
// single-word hit like "promotion" and drags the whole question into wisdom — "can i get a
// promotion this month" would score 205 for "can i" against 109 for "promotion". Score these
// near zero so any topical keyword wins, while still letting them match when nothing else does.
const VAGUE_KEYWORDS = new Set([
  "can i", "should i", "will i", "what next", "what should", "what to do",
  "how", "why", "next", "good", "bad", "better",
  // The CJK equivalents collide the same way once the raw-text fallback runs, and they collide
  // harder: CJK has no spaces, so every candidate is one "word" and only length separates them.
  "会不会", "会怎样", "能不能", "可不可以", "行吗",
  "如何", "该怎么", "怎样才能", "怎么", "该怎样", "该不该", "可以吗", "适合吗",
  "为什么", "为何", "什么原因", "怎么回事", "怎么做", "怎么处理", "要做什么",
]);

// Real keywords always score at least 101 (100 for a single word, plus its length), so this
// is a safe ceiling for "only a vague/interrogative phrase matched" — used below to stop a
// bare "should i" from blocking the general-keyword search the way a real per-chart topic
// hit rightly would.
const VAGUE_KEYWORD_SCORE = 1;

const getKeywordSpecificityScore = (keyword = "") => {
  const normalized = normalizeForMatch(keyword);
  if (!normalized) return 0;
  if (VAGUE_KEYWORDS.has(normalized)) return VAGUE_KEYWORD_SCORE;

  const words = normalized.split(" ").filter(Boolean);
  return (words.length * 100) + normalized.length;
};

const getChartValueByPeriod = (chartValues, chart, period) => {
  if (!chartValues || !chart || !period) return null;

  const keyMap = {
    wealth: "WEALTH",
    health: "HEALTH",
    relationship: "RS",
    career: "CAREER",
    wisdom: "WISDOM",
  };

  const bucket = period === "natal"
    ? (chartValues.natal || null)
    : (chartValues[period] || chartValues.monthly || chartValues.daily || chartValues.yearly || null);
  const chartKey = keyMap[chart];
  if (!bucket || !chartKey || bucket[chartKey] === undefined || bucket[chartKey] === null) {
    return null;
  }

  return toChartNumber(bucket[chartKey]);
};

const toPercentBracket = (value) => {
  if (!Number.isFinite(value)) return null;

  const safe = Math.min(100, Math.max(0, Math.ceil(value)));
  if (safe <= 20) return "0,20";
  if (safe <= 40) return "21,40";
  if (safe <= 60) return "41,60";
  if (safe <= 80) return "61,80";
  return "81,100";
};

// GetGreetings matches `score` against a hyphen-delimited 0-100 band (e.g. "21-40"),
// the same banding NatalChart uses for General categories — note it is NOT the
// comma-delimited form `toPercentBracket` builds for the guidance answer lookup.
const toGreetingScoreBand = (value) => {
  const safe = Math.min(100, Math.max(0, Math.ceil(Number(value) || 0)));
  if (safe <= 20) return "0-20";
  if (safe <= 40) return "21-40";
  if (safe <= 60) return "41-60";
  if (safe <= 80) return "61-80";
  return "81-100";
};

// chartValues buckets are keyed WEALTH/CAREER/RS/HEALTH/WISDOM; the greetings API
// expects the "General <Area>" label NatalChart sends (RS spelled out as Relationship).
const GREETING_CATEGORY_LABELS = {
  WEALTH: "General Wealth",
  CAREER: "General Career",
  RS: "General Relationship",
  HEALTH: "General Health",
  WISDOM: "General Wisdom",
};

const getGreetingCategoryLabel = (areaKey) => GREETING_CATEGORY_LABELS[areaKey] || null;

// Finds the strongest and weakest daily chart area so the opening greeting can speak
// to both. Returns null when there is no daily bucket or every area reads 0 (which is
// what a missing forecast looks like), so the caller can stay silent.
const pickDailyExtremes = (dailyValues) => {
  if (!dailyValues) return null;

  const entries = Object.keys(GREETING_CATEGORY_LABELS)
    .map((area) => ({ area, value: toChartNumber(dailyValues[area]) }))
    .filter((entry) => Number.isFinite(entry.value));
  if (entries.length === 0 || entries.every((entry) => entry.value === 0)) return null;

  let highest = entries[0];
  let lowest = entries[0];
  entries.forEach((entry) => {
    if (entry.value > highest.value) highest = entry;
    if (entry.value < lowest.value) lowest = entry;
  });

  return { highest, lowest };
};

// One greeting tip for a single area. Answers 200 even on rejection (with a
// ModelStateDictionary body), so only a real string `tip` is treated as a result.
const fetchGreetingTip = async ({ area, value, symbol, langId }) => {
  const category = getGreetingCategoryLabel(area);
  if (!category) return "";

  const payload = {
    period: "Daily",
    A2: symbol || "",
    category,
    score: toGreetingScoreBand(value),
    langid: String(langId || "2224"),
  };

  try {
    console.log(`[useFairyChat] GetGreetings request (${area}):`, payload);
    const res = await fetch(GET_GREETINGS_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    console.log(`[useFairyChat] GetGreetings response (${area}): status ${res.status}`, data);

    const row = Array.isArray(data) ? data[0] : data;
    const tip = row && typeof row.tip === "string" ? row.tip.trim() : "";
    if (!tip) {
      console.warn(`[useFairyChat] GetGreetings (${area}) returned no usable tip (200-on-failure body?)`, data);
    } else {
      console.log(`[useFairyChat] GetGreetings (${area}) relevance:`, row.relevance ?? "(none)");
    }
    return tip;
  } catch (error) {
    console.error(`[useFairyChat] GetGreetings failed (${area}):`, error);
    return "";
  }
};

// Weaves the strongest- and weakest-area daily tips into one short spoken paragraph.
// Returns the plain blank-line join whenever OpenRouter is unavailable or errors. A lone
// tip (the other area's GetGreetings call 200-rejected — see the ModelStateDictionary note
// above fetchGreetingTip) still goes through OpenRouter when a key is available: GetGreetings'
// tip text isn't reliably localized per languageId, so skipping the AI step for a single tip
// left the greeting stuck in whatever language the backend happened to send, regardless of
// the UI's selected language.
const blendGreetingTips = async ({ openRouterConfig, tips, language = { code: "en", name: "English" }, referer = "" }) => {
  const cleanTips = (tips || []).map((tip) => String(tip || "").trim()).filter(Boolean);
  if (cleanTips.length === 0) return "";

  const plainJoin = cleanTips.join("\n\n");
  if (!openRouterConfig?.apiKey) {
    console.log("[useFairyChat] Greeting blend skipped: no OpenRouter key, using plain join");
    return plainJoin;
  }

  const styleGuidance = getLanguageStyleGuidance(language);
  const isSingleTip = cleanTips.length === 1;

  const payload = {
    model: openRouterConfig.model,
    temperature: 0.5,
    reasoning: { effort: "none" },
    messages: [
      {
        role: "system",
        content: [
          "You are a warm, experienced BaZi advisor speaking directly to a client, the way a favourite teacher would.",
          isSingleTip
            ? "You are given one short daily guidance note about a life area."
            : "You are given two short daily guidance notes about two different life areas.",
          isSingleTip
            ? "Rewrite the note as ONE short flowing paragraph: 1 to 3 sentences, no more than ~50 words, no line breaks, no lists."
            : "Weave BOTH notes into ONE single flowing paragraph: 2 to 4 sentences, no more than ~70 words, no line breaks, no lists.",
          "Speak in first person, address the client as \"you\", sound spoken and encouraging rather than like a report.",
          isSingleTip
            ? "Keep the substance of the note and do not invent new predictions or advice."
            : "Keep the substance of both notes and do not invent new predictions or advice.",
          `Write the paragraph in ${language.name}, even if the note below is written in a different language.`,
          "Return only the paragraph text — no markdown, no surrounding quotes, no preamble.",
          ...(styleGuidance
            ? [`Language-specific voice guidance — this overrides the style and length notes above wherever they conflict: ${styleGuidance}`]
            : []),
        ].join(" "),
      },
      {
        role: "user",
        content: isSingleTip
          ? `Note (today's guidance): ${cleanTips[0]}`
          : `Note 1 (strongest area today): ${cleanTips[0]}\n\nNote 2 (weakest area today): ${cleanTips[1]}`,
      },
    ],
  };

  try {
    console.log("[useFairyChat] Greeting blend request model:", openRouterConfig.model);
    const response = await fetch(OPENROUTER_CHAT_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${openRouterConfig.apiKey}`,
        "HTTP-Referer": referer,
        "X-Title": openRouterConfig.appName,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      console.warn("[useFairyChat] Greeting blend request failed:", response.status, "- using plain join");
      return plainJoin;
    }

    const data = await response.json();
    const blended = stripCodeFences(getOpenRouterContentText(data)).replace(/^["']+|["']+$/g, "").trim();
    console.log("[useFairyChat] Greeting blend response:", blended || "(empty)");
    return blended || plainJoin;
  } catch (error) {
    console.warn("[useFairyChat] Greeting blend errored - using plain join:", error);
    return plainJoin;
  }
};

const buildGeneralFallbackGuidance = ({ period, chart, keyword, chartValue }) => {
  const normalizedPeriod = period || "monthly";
  const normalizedChart = chart || "wisdom";
  const chartLabel = normalizedChart === "relationship" ? "relationships" : normalizedChart;
  const bracket = toPercentBracket(chartValue) || "0,20";
  const scoreText = Number.isFinite(chartValue) ? Math.round(chartValue) : null;

  let toneLine = "Keep your pace steady and focus on clear priorities.";
  if (bracket === "81-100" || bracket === "61-80") {
    toneLine = "Momentum is supportive, so act decisively and build on what is already working.";
  } else if (bracket === "41-60") {
    toneLine = "The energy is balanced, so consistent small actions will produce the best outcome.";
  }

  const keywordLine = keyword
    ? `I could not find a keyword-specific entry for ${keyword}, so here is a broader ${normalizedPeriod} ${normalizedChart} reading.`
    : `I could not find a keyword match, so here is a broader ${normalizedPeriod} ${normalizedChart} reading.`;
  const scoreLine = scoreText === null
    ? `For this ${normalizedPeriod} cycle, center your attention on ${chartLabel} and avoid overcommitting.`
    : `For this ${normalizedPeriod} cycle, your ${chartLabel} trend is around ${scoreText}/100, so focus on what is practical and sustainable.`;
  const personalization = "Review your progress calmly before making the next move.";

  return `${keywordLine} ${toneLine} ${scoreLine} ${personalization}`;
};

const getOpenRouterContentText = (data) => {
  const content = data?.choices?.[0]?.message?.content;
  if (typeof content === "string") return content.trim();

  if (Array.isArray(content)) {
    return content
      .map((part) => (typeof part === "string" ? part : part?.text || ""))
      .join("\n")
      .trim();
  }

  return "";
};

const getChartSnapshotByPeriod = (chartValues, chart) => {
  const snapshot = {};

  OPENROUTER_PERIOD_CHOICES.forEach((period) => {
    const value = getChartValueByPeriod(chartValues, chart, period);
    snapshot[period] = {
      value: Number.isFinite(value) ? value : null,
      percentBracket: toPercentBracket(value) || "0,20",
    };
  });

  return snapshot;
};

const stripCodeFences = (rawText = "") => {
  const text = String(rawText || "").trim();
  if (!text.startsWith("```")) return text;

  return text
    .replace(/^```[a-zA-Z]*\s*/i, "")
    .replace(/\s*```$/, "")
    .trim();
};

const parseOpenRouterGuidance = (
  rawText,
  fallbackPeriod = "monthly",
  allowedPeriods = DEFAULT_PERIODS,
  allowedCharts = DEFAULT_CHARTS,
  fallbackChart = null
) => {
  const periodList = Array.isArray(allowedPeriods) && allowedPeriods.length > 0
    ? allowedPeriods
    : DEFAULT_PERIODS;
  const safeFallback = periodList.includes(fallbackPeriod)
    ? fallbackPeriod
    : (periodList.includes("monthly") ? "monthly" : periodList[0]);
  const chartList = Array.isArray(allowedCharts) && allowedCharts.length > 0
    ? allowedCharts.map((chart) => normalizeChartName(chart))
    : DEFAULT_CHARTS;
  const normalizedFallbackChart = fallbackChart ? normalizeChartName(fallbackChart) : null;
  const safeFallbackChart = normalizedFallbackChart && chartList.includes(normalizedFallbackChart)
    ? normalizedFallbackChart
    : null;

  const cleaned = stripCodeFences(rawText);
  if (!cleaned) {
    return { period: safeFallback, answer: "", keyword: null, charts: safeFallbackChart ? [safeFallbackChart] : [], followUp: null, suggestions: [] };
  }

  const parseJsonPayload = (payloadText) => {
    const parsed = JSON.parse(payloadText);
    const period = normalizeForMatch(parsed?.period || "");
    const normalizedPeriod = periodList.includes(period) ? period : safeFallback;
    const keyword = String(parsed?.keyword || parsed?.focusKeyword || parsed?.focus || "").trim() || null;

    const rawCharts = parsed?.charts ?? parsed?.chart ?? parsed?.categories ?? null;
    const chartValues = Array.isArray(rawCharts)
      ? rawCharts
      : (rawCharts ? [rawCharts] : []);
    const normalizedCharts = chartValues
      .map((item) => normalizeChartName(item))
      .filter((item, index, arr) => !!item && chartList.includes(item) && arr.indexOf(item) === index);

    if (normalizedCharts.length === 0 && safeFallbackChart) {
      normalizedCharts.push(safeFallbackChart);
    }

    const answer = String(parsed?.answer || parsed?.guidance || parsed?.text || "").trim();
    const followUp = String(parsed?.followUp || parsed?.follow_up || parsed?.followUpQuestion || "").trim() || null;
    const rawSuggestions = parsed?.suggestions ?? parsed?.options ?? null;
    const suggestions = Array.isArray(rawSuggestions)
      ? rawSuggestions.map((s) => String(s || "").trim()).filter(Boolean).slice(0, 4)
      : [];
    return { period: normalizedPeriod, answer, keyword, charts: normalizedCharts, followUp, suggestions };
  };

  try {
    return parseJsonPayload(cleaned);
  } catch {
    // Fall through to JSON block extraction.
  }

  const jsonBlock = cleaned.match(/\{[\s\S]*\}/);
  if (jsonBlock && jsonBlock[0]) {
    try {
      return parseJsonPayload(jsonBlock[0]);
    } catch {
      // Fall through to plain text fallback.
    }
  }

  // JSON-looking text that failed both parses is a broken model reply (e.g. a bare
  // `{"  }  ]  }` skeleton), not prose — showing it would dump brackets into the chat bubble.
  const looksLikeBrokenJson = /^[{[]/.test(cleaned) || !/[\p{L}\p{N}]/u.test(cleaned);

  return {
    period: safeFallback,
    answer: looksLikeBrokenJson ? "" : cleaned,
    keyword: null,
    charts: safeFallbackChart ? [safeFallbackChart] : [],
    followUp: null,
    suggestions: [],
  };
};

const resolvePeriodWithOpenRouter = async ({
  openRouterConfig,
  userText,
  fallbackPeriod = "monthly",
  allowedPeriods = DEFAULT_PERIODS,
  referer = "",
}) => {
  const periodList = Array.isArray(allowedPeriods) && allowedPeriods.length > 0
    ? allowedPeriods
    : DEFAULT_PERIODS;

  const payload = {
    model: openRouterConfig.model,
    temperature: 0,
    reasoning: { effort: "none" },
    response_format: { type: "json_object" },
    messages: [
      {
        role: "system",
        content: [
          "You classify time period for a guidance request.",
          `Allowed periods: ${periodList.join(", ")}.`,
          "Choose exactly one period based on the user's wording.",
          `If the question does not clearly indicate a time period, return "${fallbackPeriod}".`,
          "Never guess a period from the topic alone.",
          "Return strict JSON only: {\"period\":\"daily|monthly|yearly|natal\"}",
          "No markdown fences. No extra text.",
        ].join(" "),
      },
      {
        role: "user",
        content: `Question: ${userText}`,
      },
    ],
  };

  try {
    const response = await fetch(OPENROUTER_CHAT_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${openRouterConfig.apiKey}`,
        "HTTP-Referer": referer,
        "X-Title": openRouterConfig.appName,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) return fallbackPeriod;

    const data = await response.json();
    const content = getOpenRouterContentText(data);
    const parsed = parseOpenRouterGuidance(content, fallbackPeriod, periodList);
    return parsed.period || fallbackPeriod;
  } catch {
    return fallbackPeriod;
  }
};

// The pre-AI general-keyword pass (textMatchesKeyword) only catches literal/near-literal
// wording, so a paraphrase like "what suits me" never matches the catalog entry "suitable
// jobs, career wealth, career luck" even though it is clearly asking about it. This asks
// the AI to match by meaning instead. It must copy the chosen entry back verbatim — the
// model returning invented text instead of a catalog string is treated as no match, since
// only a real bundle can be posted to GET_GUIDANCE_ANSWER_URL.
const resolveGeneralKeywordWithOpenRouter = async ({
  openRouterConfig,
  userText,
  generalKeywords = [],
  referer = "",
}) => {
  if (!Array.isArray(generalKeywords) || generalKeywords.length === 0) return null;

  const payload = {
    model: openRouterConfig.model,
    temperature: 0,
    // "low", not "none" — this call has to catch paraphrases (its own prompt gives one as
    // an example), and the zero-effort setting was letting exactly that case slip through.
    reasoning: { effort: "low" },
    response_format: { type: "json_object" },
    messages: [
      {
        role: "system",
        content: [
          "You match a user's question against a fixed catalog of general life-guidance topics.",
          "Each catalog entry may bundle several synonyms separated by commas - treat the whole entry as one topic.",
          "Pick the single entry whose topic the question is actually asking about, even if the wording differs",
          "(for example, a question asking what suits the person should match an entry about suitable jobs).",
          "If no entry is a reasonable match, return null.",
          'Return strict JSON only: {"match":"<catalog entry, copied exactly>"} or {"match":null}',
          "The match value must be copied EXACTLY from the catalog - do not paraphrase, translate, or invent text.",
          "No markdown fences. No extra text.",
        ].join(" "),
      },
      {
        role: "user",
        content: `Catalog:\n${generalKeywords.map((entry) => `- ${entry}`).join("\n")}\n\nQuestion: ${userText}`,
      },
    ],
  };

  const readMatch = (jsonText) => {
    const parsed = JSON.parse(jsonText);
    return typeof parsed?.match === "string" ? parsed.match.trim() : null;
  };

  try {
    const response = await fetch(OPENROUTER_CHAT_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${openRouterConfig.apiKey}`,
        "HTTP-Referer": referer,
        "X-Title": openRouterConfig.appName,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) return null;

    const cleaned = stripCodeFences(getOpenRouterContentText(await response.json()));
    if (!cleaned) return null;

    let rawMatch = null;
    try {
      rawMatch = readMatch(cleaned);
    } catch {
      const jsonBlock = cleaned.match(/\{[\s\S]*\}/);
      if (!jsonBlock) return null;
      try {
        rawMatch = readMatch(jsonBlock[0]);
      } catch {
        return null;
      }
    }
    if (!rawMatch) return null;

    const normalizedMatch = normalizeForMatch(rawMatch);
    return generalKeywords.find((entry) => normalizeForMatch(entry) === normalizedMatch) || null;
  } catch {
    return null;
  }
};

// Mirrors resolveGeneralKeywordWithOpenRouter above, but against the per-chart topic catalogs.
// Local matching (inferChartAndKeyword/findKeywordMatch) is pure literal string containment, so
// a real topical question phrased in different words than the catalog ("can't talk to my kids"
// vs the relationship catalog's "children" entry) never reaches that chart's own keyword-seeded
// content — it silently falls through to a generic reading instead. Asking the model to match
// semantically against the real per-chart catalog (same trick already used for the general
// bucket) resolves synonyms and paraphrases the literal pass can't.
const resolveChartKeywordWithOpenRouter = async ({
  openRouterConfig,
  userText,
  keywordsByChart = {},
  chartList = [],
  referer = "",
}) => {
  const charts = chartList.filter((chart) => Array.isArray(keywordsByChart[chart]) && keywordsByChart[chart].length > 0);
  if (charts.length === 0) return null;

  const catalogLines = charts.flatMap((chart) => keywordsByChart[chart].map((entry) => `- [${chart}] ${entry}`));
  if (catalogLines.length === 0) return null;

  const payload = {
    model: openRouterConfig.model,
    temperature: 0,
    reasoning: { effort: "low" },
    response_format: { type: "json_object" },
    messages: [
      {
        role: "system",
        content: [
          "You match a user's question against a fixed catalog of life-guidance topics, each tagged with the chart it belongs to.",
          "Each catalog entry may bundle several synonyms separated by commas - treat the whole entry as one topic.",
          "Pick the single entry whose topic the question is actually asking about, even if the wording differs",
          "(for example, a question about not being able to talk to one's kids should match a \"children\" entry).",
          "If no entry is a reasonable match, return null.",
          'Return strict JSON only: {"chart":"<chart tag, copied exactly>","match":"<catalog entry, copied exactly>"} or {"chart":null,"match":null}',
          "The chart and match values must be copied EXACTLY from the catalog - do not paraphrase, translate, or invent text.",
          "No markdown fences. No extra text.",
        ].join(" "),
      },
      {
        role: "user",
        content: `Catalog:\n${catalogLines.join("\n")}\n\nQuestion: ${userText}`,
      },
    ],
  };

  const readResult = (jsonText) => {
    const parsed = JSON.parse(jsonText);
    return {
      chart: typeof parsed?.chart === "string" ? parsed.chart.trim() : null,
      match: typeof parsed?.match === "string" ? parsed.match.trim() : null,
    };
  };

  try {
    const response = await fetch(OPENROUTER_CHAT_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${openRouterConfig.apiKey}`,
        "HTTP-Referer": referer,
        "X-Title": openRouterConfig.appName,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      console.warn("[useFairyChat] resolveChartKeywordWithOpenRouter request failed:", response.status);
      return null;
    }

    const cleaned = stripCodeFences(getOpenRouterContentText(await response.json()));
    if (!cleaned) return null;

    let rawResult = null;
    try {
      rawResult = readResult(cleaned);
    } catch {
      const jsonBlock = cleaned.match(/\{[\s\S]*\}/);
      if (!jsonBlock) {
        console.warn("[useFairyChat] resolveChartKeywordWithOpenRouter: unparseable response:", cleaned);
        return null;
      }
      try {
        rawResult = readResult(jsonBlock[0]);
      } catch {
        console.warn("[useFairyChat] resolveChartKeywordWithOpenRouter: unparseable response:", cleaned);
        return null;
      }
    }
    if (!rawResult?.chart || !rawResult?.match) {
      console.log("[useFairyChat] resolveChartKeywordWithOpenRouter: model found no match:", rawResult);
      return null;
    }

    // The model is asked to copy the "[chart] entry" catalog line's chart tag exactly, but
    // sometimes carries the surrounding brackets along with it — strip them before comparing
    // instead of failing the whole lookup over stray punctuation the model added.
    const normalizedChart = normalizeChartName(rawResult.chart.replace(/[[\]"]/g, "").trim());
    const normalizedMatch = normalizeForMatch(rawResult.match);

    const directBundle = (keywordsByChart[normalizedChart] || [])
      .find((entry) => normalizeForMatch(entry) === normalizedMatch);
    if (directBundle) return { chart: normalizedChart, keyword: directBundle };

    // Chart tag didn't line up (model mangled it, or named a chart outside chartList) — the
    // matched entry text is still the load-bearing part, so fall back to whichever catalog
    // actually contains it rather than discarding a correct keyword match over a bad tag.
    for (const [chart, entries] of Object.entries(keywordsByChart)) {
      const fallbackBundle = (entries || []).find((entry) => normalizeForMatch(entry) === normalizedMatch);
      if (fallbackBundle) {
        console.warn(
          "[useFairyChat] resolveChartKeywordWithOpenRouter: chart tag mismatch, recovered via catalog scan:",
          { returnedChart: rawResult.chart, resolvedChart: chart, match: fallbackBundle }
        );
        return { chart, keyword: fallbackBundle };
      }
    }

    console.warn("[useFairyChat] resolveChartKeywordWithOpenRouter: match text not found in any catalog:", rawResult);
    return null;
  } catch (error) {
    console.warn("[useFairyChat] resolveChartKeywordWithOpenRouter errored:", error);
    return null;
  }
};

// findSafetyScenarioMatch only catches a scenario whose literal keywords appear in THIS
// message, so it can't catch a paraphrase, or a one-word "yes"/"every day" answer to a crisis
// question the advisor just asked ("are you thinking about hurting yourself?"). This is the
// scenario-level analogue of resolveGeneralKeywordWithOpenRouter above: only tried once the
// literal check has already come back empty, so the common case (no crisis, or a literal hit)
// never pays for the extra round trip. Deliberately biased toward matching when unsure — a
// false alarm here costs one canned message; a miss costs a real one.
const buildSafetySeverityCatalog = (scenarios = []) => scenarios.map((scenario) => (
  `${scenario.scenario}: ${scenario.keywordGroups.map((group) => group.slice(0, 8).join(", ")).join(" / ")}`
));

const resolveSafetyScenarioWithOpenRouter = async ({
  openRouterConfig,
  userText,
  history = [],
  scenarios = [],
  referer = "",
}) => {
  if (!Array.isArray(scenarios) || scenarios.length === 0) return null;

  const transcript = (Array.isArray(history) ? history : [])
    .filter((m) => m && typeof m.text === "string" && m.text.trim())
    .slice(-HISTORY_TURNS_FOR_AI)
    .map((m) => `${m.sender === "user" ? "Client" : "Advisor"}: ${m.text.trim().slice(0, HISTORY_CHARS_PER_TURN)}`)
    .join("\n");

  const payload = {
    model: openRouterConfig.model,
    temperature: 0,
    reasoning: { effort: "low" },
    response_format: { type: "json_object" },
    messages: [
      {
        role: "system",
        content: [
          "You are a safety triage classifier for a metaphysical-advice chat app. Decide whether the client's latest message, read together with the conversation so far, genuinely describes one of the crisis scenarios below — not just whether it contains similar words.",
          "Judge by meaning and context, not literal wording: catch a paraphrase, and catch a short reply (\"yes\", \"every night\", \"he did it again\") that only makes sense as an answer to a crisis question the advisor just asked earlier in the conversation.",
          "Do NOT match idiomatic, joking, hypothetical, fictional, or metaphorical use of similar words — \"this deadline is killing me\", discussing a movie or book plot, or ordinary metaphysics chat about \"fighting for a relationship\" are not matches.",
          "If you are genuinely unsure whether this is a real crisis, prefer matching over missing one — the cost of a false alarm here is far lower than the cost of missing a real one.",
          `Scenarios (name: representative words/phrases):\n${buildSafetySeverityCatalog(scenarios).join("\n")}`,
          'Return strict JSON only: {"scenario":"<scenario name, copied exactly>"} or {"scenario":null}',
          "The scenario name must be copied EXACTLY from the list above - do not paraphrase or invent one.",
          "No markdown fences. No extra text.",
        ].join(" "),
      },
      {
        role: "user",
        content: `${transcript ? `Conversation so far (oldest first):\n${transcript}\n\n` : ""}Client's latest message: ${userText}`,
      },
    ],
  };

  const readScenario = (jsonText) => {
    const parsed = JSON.parse(jsonText);
    return typeof parsed?.scenario === "string" ? parsed.scenario.trim() : null;
  };

  try {
    const response = await fetch(OPENROUTER_CHAT_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${openRouterConfig.apiKey}`,
        "HTTP-Referer": referer,
        "X-Title": openRouterConfig.appName,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) return null;

    const cleaned = stripCodeFences(getOpenRouterContentText(await response.json()));
    if (!cleaned) return null;

    let rawScenario = null;
    try {
      rawScenario = readScenario(cleaned);
    } catch {
      const jsonBlock = cleaned.match(/\{[\s\S]*\}/);
      if (!jsonBlock) return null;
      try {
        rawScenario = readScenario(jsonBlock[0]);
      } catch {
        return null;
      }
    }
    if (!rawScenario) return null;

    return scenarios.find((scenario) => scenario.scenario === rawScenario) || null;
  } catch {
    return null;
  }
};

// The keyword catalog and the GET_GUIDANCE_ANSWER_URL lookup only match English text, so a
// question typed in another UI language is translated to English purely for matching. The
// translation is internal: the chat bubble keeps the user's own wording, and the fairy still
// answers in their language. Returns "" on any failure so callers fall back to the raw text.
const translateQuestionToEnglish = async ({
  openRouterConfig,
  userText,
  sourceLanguageName,
  referer = "",
}) => {
  const payload = {
    model: openRouterConfig.model,
    temperature: 0,
    reasoning: { effort: "none" },
    response_format: { type: "json_object" },
    messages: [
      {
        role: "system",
        content: [
          `You translate a short user question from ${sourceLanguageName} into English.`,
          "Translate literally and keep the topic wording intact so it can be matched against an English keyword list.",
          "Do not answer the question, do not add or remove meaning, and do not explain anything.",
          "If the text is already English, return it unchanged.",
          'Return strict JSON only: {"english":"<translated question>"}',
          "No markdown fences. No extra text.",
        ].join(" "),
      },
      {
        role: "user",
        content: userText,
      },
    ],
  };

  const readEnglish = (jsonText) => {
    const parsed = JSON.parse(jsonText);
    return String(parsed?.english || parsed?.translation || "").trim();
  };

  try {
    const response = await fetch(OPENROUTER_CHAT_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${openRouterConfig.apiKey}`,
        "HTTP-Referer": referer,
        "X-Title": openRouterConfig.appName,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) return "";

    const cleaned = stripCodeFences(getOpenRouterContentText(await response.json()));
    if (!cleaned) return "";

    try {
      return readEnglish(cleaned);
    } catch {
      const jsonBlock = cleaned.match(/\{[\s\S]*\}/);
      if (!jsonBlock) return "";
      try {
        return readEnglish(jsonBlock[0]);
      } catch {
        return "";
      }
    }
  } catch {
    return "";
  }
};

const getOpenRouterConfig = () => {
  const envApiKey = (process.env.REACT_APP_OPENROUTER_API_KEY || "").trim();
  const runtimeApiKey =
    typeof window !== "undefined"
      ? (localStorage.getItem(OPENROUTER_RUNTIME_KEY_STORAGE) || "").trim()
      : "";

  const model =
    (process.env.REACT_APP_OPENROUTER_MODEL || OPENROUTER_DEFAULT_MODEL).trim()
    || OPENROUTER_DEFAULT_MODEL;
  const appName =
    (process.env.REACT_APP_OPENROUTER_APP_NAME || OPENROUTER_DEFAULT_APP_NAME).trim()
    || OPENROUTER_DEFAULT_APP_NAME;
  const apiKey = envApiKey || runtimeApiKey;

  return {
    apiKey,
    model,
    appName,
    source: envApiKey ? "env" : runtimeApiKey ? "localStorage" : "none",
  };
};

const buildOpenRouterMessages = ({
  userText,
  period,
  availablePeriods,
  availableCharts,
  chart,
  keyword,
  profile,
  periodScores,
  seedAnswer = null,
  language = { code: "en", name: "English" },
  periodIsExplicit = false,
  history = [],
}) => {
  const normalizedPeriods = Array.isArray(availablePeriods) && availablePeriods.length > 0
    ? availablePeriods
    : DEFAULT_PERIODS;

  const normalizedCharts = Array.isArray(availableCharts) && availableCharts.length > 0
    ? availableCharts.map((item) => normalizeChartName(item))
    : DEFAULT_CHARTS;

  const styleGuidance = getLanguageStyleGuidance(language);

  const context = {
    currentDate: new Date().toISOString().slice(0, 10),
    candidatePeriod: period || "monthly",
    availablePeriods: normalizedPeriods,
    candidateChart: chart || "not specified",
    availableCharts: normalizedCharts,
    keyword: keyword || "not specified",
    profile: {
      gender: (profile?.gender || "unknown").toLowerCase(),
      strengthCategory: profile?.strengthCategory || "unknown",
      natalSymbol: profile?.lunarData?.A2 || "unknown",
      zodiacSign: profile?.zodiacSign || "unknown",
    },
    periodScores: periodScores || {},
  };
  console.info("[buildOpenRouterMessages] Built context for OpenRouter:", context);

  // Each call is otherwise stateless, so a one-word reply like "languages" to "which subject
  // are you least confident about?" reads as a brand-new topic. Recent turns go in as a
  // transcript on the user turn rather than as assistant messages, so the model isn't primed
  // to answer in plain prose instead of the JSON shape.
  const transcript = (Array.isArray(history) ? history : [])
    .filter((m) => m && typeof m.text === "string" && m.text.trim())
    .slice(-HISTORY_TURNS_FOR_AI)
    .map((m) => `${m.sender === "user" ? "Client" : "Advisor"}: ${m.text.trim().slice(0, HISTORY_CHARS_PER_TURN)}`)
    .join("\n");
  const transcriptBlock = transcript ? `Conversation so far (oldest first):\n${transcript}\n\n` : "";

  return [
    {
      role: "system",
      content: [
      "You are a warm, experienced BaZi career advisor speaking directly to a student — think a senior practitioner after 40 years in the field: unhurried, sure of what she's seeing, comfortable saying the hard thing plainly.",
      "Speak in first person and address the listener as \"you.\" Sound like you're talking, not writing — conversational, warm, occasionally blunt.",
      "Season it with natural check-ins like \"okay?\", \"you know?\", \"right?\" (or, in Chinese, 对不对/是不是/嘛) — but keep this restrained per the language-specific voice guidance below: never stack more than one in a response, and let roughly half your responses land with none at all.",
      "Don't just state what's going on — explain the why behind it first, then land it with a concrete real-world consequence (\"so what this means is...\", \"this is why you might feel...\").",
      "Be direct and confident. Give clear guidance on what to do or avoid — don't hedge. If something needs to be said bluntly (\"don't do this,\" \"never say that\"), say it bluntly.",
      "If what you're seeing is cautionary, be honest about the risk — don't undersell it — but always follow with a practical next step so the student isn't left just sitting with worry.",
      "When you introduce a genuinely established BaZi concept or mechanism, frame it like teaching: \"this is what we call...\", \"here's the thing about...\". Use this only to explain how something works — never to coin a name for the student's own current phase or status.",
      "Never name-drop structural jargon as if labeling a category for the student — no \"BaZi,\" \"pillar,\" \"Four Pillars,\" \"chart,\" \"reading\" (or \"readings\"), and never recite or invent a tier/phase/status label (e.g. \"your abundance era,\" \"a growth season,\" \"you're in X\") even if it sounds like a horoscope tier name you made up on the spot. Also avoid \"energy\" as a catch-all noun for luck or fortune. Translate all of this into plain, everyday language about their life and concrete outcomes instead. If you need to refer to what you're doing, call it \"what I'm seeing\" or \"this conversation,\" never the system's name or format.",
      "Never state a numeric score or rating out loud (e.g. \"your score is 70\"), even if one appears in the context below — describe the strength or weakness in plain qualitative terms instead.",
      "Never mention anything zodiac or astrological out loud — no animal signs (\"you're a Tiger\", \"as a Rat\"), no animal years (\"this Goat year\", \"the Year of the Horse\"), no \"zodiac\", \"sign\", \"horoscope\", \"clash year\", \"Tai Sui\", elements or stems (\"Yang Wood\", \"your Fire\"), \"Day Master\", or strength labels (\"a strong/weak ...\"). This holds even if the client names their own sign or the year's animal — answer their question without repeating it. That data is only there to shape your advice; the client should hear plain talk about their life, not the reasons behind it.",
      "Base every answer on the client's own profile (gender, strengthCategory, natalSymbol, zodiacSign) and periodScores from the context below — let that data shape the reading and how confident or cautious you sound, but never read the raw field names, values, or scores back to the client; translate them into plain spoken guidance instead.",
      "You only ever speak as this advisor, and you only address the client's wealth, career, relationships, health, and wisdom through Bazi, Feng Shui, and metaphysics.",
      "If the user's question is unrelated to those life areas (general trivia, math, coding, homework, current events, or any other topic outside metaphysical guidance), do not answer it directly and do not explain the topic. Instead, briefly and warmly note that it falls outside what you read, then pivot straight into a short metaphysical reading grounded in the client's own profile and periodScores from the context below, tying it to whichever of wealth, career, relationship, health, or wisdom fits best. The pivoted reading must still follow the same JSON shape and sentence-count rules as a normal answer.",
      "Read the user question in the light of the conversation so far. If it is a short reply to your previous followUp question (naming a subject, option, or person you just asked about), treat it as that answer and stay on the thread already under way — the same goal, situation, and timeframe — rather than opening a new general topic. For example, if you asked which O-level subject they feel least confident in and they say \"languages\", talk about preparing for their language papers, not about learning languages in general.",
      "Infer period, keyword, and one-or-more relevant charts from the user question using natural language understanding.",
      `Allowed periods: ${normalizedPeriods.join(", ")}.`,
      `Allowed charts: ${normalizedCharts.join(", ")}.`,
      "Charts are life areas, not topic labels: pick the chart by what the client is actually asking about, never by a word in the question that merely resembles a chart name.",
      "A question about an industry, profession, employer, job, or business sector belongs to career even when its name echoes another chart — \"the healthcare industry\", \"working as a nurse\", or \"opening a wellness studio\" are career questions, not health questions.",
      "Only choose health when the client asks about their own body, energy, illness, recovery, or mental wellbeing; likewise choose wealth for money itself rather than for the finance industry, and relationship for their own bonds rather than for the wedding or dating business.",
      "If the keyword reasonably spans multiple charts, include all relevant charts (max 3) and combine them in one integrated answer.",
      "Respond as strict JSON only with this shape:",
      '{"period":"daily|monthly|yearly|natal","keyword":"<detected keyword>","charts":["wealth|health|relationship|career|wisdom"],"answer":"<one clear insight and one clear piece of advice, said once each, in a single paragraph — length capped by the language-specific voice guidance below>","followUp":"<one contextual follow-up question directly referencing what was just answered>","suggestions":["<short option 1>","<short option 2>","<short option 3>"]}',
      "Do not wrap JSON in markdown fences.",
      `Write the "answer", "followUp" and "suggestions" values in ${language.name} — that is the language the client reads.`,
      'Always write the "keyword" value in English, and keep the "period" and "charts" values exactly as spelled in the allowed lists above, regardless of the answer language. Those three fields are looked up against an English database, not shown to the client.',
      "If ambiguous, use candidatePeriod and candidateChart from context.",
      periodIsExplicit
        ? "The client's question names a timeframe (e.g. today, this month, this year), so it's fine to speak in terms of that period."
        : "The client's question does NOT name a specific timeframe, so do not say \"this month\", \"today\", \"this year\", or any other period phrase in the answer or followUp — candidatePeriod is only an internal lookup key, not something to state out loud. Speak in general, timeless terms instead.",
      "The answer field's word/character target from the language-specific voice guidance below is a hard ceiling for the ENTIRE answer, not per sentence or per beat — the why, the consequence, and any aside all count together toward that cap. Write it as ONE paragraph; a second short paragraph is only acceptable if the whole thing still fits the cap, and never write three or more. If it doesn't fit, drop the aside first, then compress the why to a phrase — never keep everything by adding paragraphs. This is a chat message, not a lecture: one clear insight, one clear piece of advice, said once each, not restated at the end. The followUp field must be a specific, contextual question referencing the topic just answered. The suggestions field must contain 2-4 short clickable options the user can pick to continue — they should directly relate to the followUp question. For example if the topic is studying, suggestions could be ['a specific subject', 'study methods', 'exam preparation', 'long-term learning']. Keep each suggestion under 5 words.",
      ...(styleGuidance
        ? [`Language-specific voice guidance for the "answer", "followUp" and "suggestions" values — this overrides the general style and length notes above wherever they conflict: ${styleGuidance}`]
        : []),
      ].join(" "),
    },
    {
      role: "user",
      // The seed comes out of the guidance DB, which is English-only, and keyword chips send an
      // English label as the question. Either one puts English directly in front of the model,
      // and it mirrors that instead of the system prompt's language line — so the reply language
      // is restated here, in the last thing the model reads, and the seed is labelled as
      // reference material rather than a template to copy.
      content: seedAnswer
        ? `${transcriptBlock}User question: ${userText}\n\nGuidance context:\n${JSON.stringify(context, null, 2)}\n\nBase answer to expand upon — this is English source material, not the language to answer in:\n${seedAnswer}\n\nRetell this base answer in your own voice as the advisor, written entirely in ${language.name}. Speak directly to them. Keep it to 2-3 sentences, no more than ~60 words, in a single paragraph. Write in ${language.name} even if the question and the base answer above are in English.`
        : `${transcriptBlock}User question: ${userText}\n\nGuidance context:\n${JSON.stringify(context, null, 2)}\n        Give your reading in your own voice as the advisor, written entirely in ${language.name}.\n        Speak directly to them. Keep it to 2-3 sentences, no more than ~60 words, in a single paragraph. Write in ${language.name} even if the question above is in English.`,
    },
  ];
};

// Leak detection for the two output rules that the system prompt states but a long prompt
// can't enforce every time: the "never name-drop structural jargon" rule, and — when the
// client's own question didn't name a timeframe — the no-period-words rule. Both are
// detect-and-re-ask rather than find-and-replace, because every word involved has an innocent
// everyday sense in real advice ("twenty minutes reading before bed", "chart your spending",
// "set up a monthly budget review"). A detector that is occasionally wrong costs one extra
// call; a rewriter that is occasionally wrong ships mangled English to the chat bubble.
// None of these patterns carry the /g flag on purpose: .test() on a global regex advances
// lastIndex between calls, so a shared module-level pattern would start skipping matches.

// Determiner-anchored, because the bare nouns are nearly always innocent: "chart your
// progress" and "make reading a habit" are advice, while "your chart" and "this reading" are
// the system's format by another name. "BaZi" and "Four Pillars" are structural by
// definition, so they always count. "the pillar of ..." is excluded — that's the everyday
// metaphor, not the system's pillars. The CJK terms are here because the answer is written in
// the client's language, so an English-only detector leaves every non-English answer
// unchecked.
const JARGON_LEAK_PATTERNS = [
  /\bbazi\b/i,
  /\bfour\s+pillars\b/i,
  /\b(?:year|month|day|hour)\s+pillars?\b/i,
  /\b(?:your|my|the|this|that)\s+pillars?\b(?!\s+of\b)/i,
  /\b(?:your|my|the|this|that|their|his|her)\s+(?:natal\s+|birth\s+|bazi\s+)?charts?\b/i,
  /\b(?:wealth|health|relationship|career|wisdom|natal|birth)\s+charts?\b/i,
  /\b(?:your|my|the|this|that|a)\s+readings?\b(?!\s+(?:habits?|speed|lists?|materials?|glasses|comprehension|level|skills?)\b)/i,
  /八字|四柱|命盘|命盤/,
];

const hasJargonLeak = (text) => JARGON_LEAK_PATTERNS.some((pattern) => pattern.test(text));

// The client's zodiac sign and the year's animal feed the model as context only. Bare animal
// names are everyday words ("walk your dog", "the horse you backed"), so each pattern is
// anchored to a zodiac framing: a year, a sign, or the client being called one. The CJK and
// Indonesian terms cover answers written in the client's own language.
const ZODIAC_ANIMALS = "rats?|ox|oxen|tigers?|rabbits?|dragons?|snakes?|horses?|goats?|sheep|rams?|monkeys?|roosters?|dogs?|pigs?|boars?";
const ZODIAC_LEAK_PATTERNS = [
  /\b(?:zodiac|horoscope|day\s+master|tai\s+sui|clash\s+year)\b/i,
  /\b(?:your|my|animal|chinese|birth)\s+signs?\b/i,
  new RegExp(`\\byears?\\s+of\\s+the\\s+(?:${ZODIAC_ANIMALS})\\b`, "i"),
  new RegExp(`\\b(?:${ZODIAC_ANIMALS})\\s+(?:years?|signs?|people|natives)\\b`, "i"),
  new RegExp(`\\b(?:you're|you\\s+are|as)\\s+(?:a|an)\\s+(?:\\w+\\s+)?(?:${ZODIAC_ANIMALS})\\b`, "i"),
  new RegExp(`\\b(?:${ZODIAC_ANIMALS})\\s+like\\s+you\\b`, "i"),
  /\b(?:yin|yang)\s+(?:wood|fire|earth|metal|water)\b/i,
  /生肖|属相|本命年|太岁|日主|属[鼠牛虎兔龙蛇马羊猴鸡狗猪]|[鼠牛虎兔龙蛇马羊猴鸡狗猪]年/,
  /\bshio\b|\btahun\s+(?:tikus|kerbau|macan|harimau|kelinci|naga|ular|kuda|kambing|monyet|ayam|anjing|babi)\b/i,
];

const hasZodiacLeak = (text) => ZODIAC_LEAK_PATTERNS.some((pattern) => pattern.test(text));

// Period words are legitimate in real advice too ("set up a monthly budget review"), which is
// why this is only consulted when periodIsExplicit is false — when the client named a
// timeframe themselves, saying it back is correct, not a leak.
const PERIOD_LEAK_PATTERN = /\b(daily|monthly|yearly|annual|annually|natal|today|this month|this week|this year)\b/i;

// Checks everything the client actually sees. answer and followUp are concatenated into the
// chat bubble, and suggestions are rendered as clickable chips (see setSuggestions below), so
// a leak in a chip is exactly as visible as one in the answer. Returns one phrase per broken
// rule, so a single retry can carry every correction instead of costing a round trip each.
const collectGuidanceLeaks = (parsed, periodIsExplicit) => {
  const visibleText = [parsed?.answer, parsed?.followUp, ...(parsed?.suggestions || [])]
    .filter(Boolean)
    .join(" ");
  if (!visibleText) return [];

  const leaks = [];
  if (hasJargonLeak(visibleText)) {
    leaks.push(
      "named the system or its format out loud (e.g. \"BaZi\", \"Four Pillars\", \"your chart\", \"this reading\") — call it \"what I'm seeing\" or drop the label and speak in plain, everyday terms about their life"
    );
  }
  if (hasZodiacLeak(visibleText)) {
    leaks.push(
      "mentioned zodiac details out loud (e.g. \"you're a Tiger\", \"this Goat year\", \"your sign\", \"Yang Wood\") — keep the advice but drop every zodiac animal, sign, year animal, element, and strength label"
    );
  }
  if (!periodIsExplicit && PERIOD_LEAK_PATTERN.test(visibleText)) {
    leaks.push(
      "named a specific timeframe (e.g. \"daily\", \"monthly\", \"today\", \"this month\") even though the question didn't ask about one — speak in general, timeless terms instead"
    );
  }
  return leaks;
};

const fetchGuidanceCompletion = async ({ openRouterConfig, messages, temperature, referer, reasoningEffort = "low" }) => {
  const payload = {
    model: openRouterConfig.model,
    messages,
    temperature,
    reasoning: { effort: reasoningEffort },
    response_format: { type: "json_object" },
  };
  console.log("[useFairyChat] OpenRouter request model:", openRouterConfig.model);
  console.log("[useFairyChat] OpenRouter request payload:", payload);

  const response = await fetch(OPENROUTER_CHAT_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${openRouterConfig.apiKey}`,
      "HTTP-Referer": referer,
      "X-Title": openRouterConfig.appName,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`OpenRouter failed: ${response.status}`);

  const data = await response.json();
  console.log("[useFairyChat] OpenRouter response:", data);
  return getOpenRouterContentText(data);
};

// Named distinctly from the per-message `requestGuidanceAnswer` closure defined inside
// useFairyChat() below (different signature: positional targetPeriod/targetKeyword, orchestrates
// the whole seed+enrich flow) — reusing that name here would silently shadow it at the call
// sites inside that closure and recurse into itself instead of calling this helper.
const requestGuidanceCompletion = async ({
  openRouterConfig,
  messages,
  temperature,
  referer,
  fallbackPeriod,
  allowedPeriods,
  allowedCharts,
  fallbackChart,
  periodIsExplicit,
}) => {
  const rawText = await fetchGuidanceCompletion({ openRouterConfig, messages, temperature, referer });
  let parsed = parseOpenRouterGuidance(rawText, fallbackPeriod, allowedPeriods, allowedCharts, fallbackChart);

  // Some models (seen with deepseek-v4-flash) spend the whole reply on `reasoning` and return a
  // degenerate JSON skeleton as `content` when reasoning and json_object are combined. Re-ask
  // once with reasoning off, which is the setting the period classifier already uses safely.
  if (!parsed.answer) {
    console.warn("[useFairyChat] Guidance reply had no usable answer, retrying with reasoning off:", rawText);
    try {
      const noReasoningText = await fetchGuidanceCompletion({
        openRouterConfig,
        messages,
        temperature,
        referer,
        reasoningEffort: "none",
      });
      parsed = parseOpenRouterGuidance(noReasoningText, fallbackPeriod, allowedPeriods, allowedCharts, fallbackChart);
    } catch (emptyAnswerRetryError) {
      console.warn("[useFairyChat] No-reasoning retry failed:", emptyAnswerRetryError);
    }
  }

  const leaks = collectGuidanceLeaks(parsed, periodIsExplicit);
  if (leaks.length === 0) return parsed;

  console.warn("[useFairyChat] Guidance leak detected, retrying once:", { leaks, parsed });
  try {
    const retryText = await fetchGuidanceCompletion({
      openRouterConfig,
      temperature,
      referer,
      messages: [
        ...messages,
        {
          role: "user",
          content: [
            "Your last reply broke the rules below:",
            ...leaks.map((leak) => `- It ${leak}.`),
            "Send the same JSON answer again with that fixed. The fix must hold in \"answer\", in \"followUp\", and in every entry of \"suggestions\" — the client sees all three. Return only the corrected JSON, no other text.",
          ].join("\n"),
        },
      ],
    });
    const retryParsed = parseOpenRouterGuidance(retryText, fallbackPeriod, allowedPeriods, allowedCharts, fallbackChart);
    // Only take the retry if it's a strict improvement: one that leaks just as much has no
    // upside, and any rewrite risks losing what the first answer got right.
    if (retryParsed.answer && collectGuidanceLeaks(retryParsed, periodIsExplicit).length < leaks.length) {
      parsed = retryParsed;
    } else {
      console.warn("[useFairyChat] Leak retry was not an improvement, keeping the original answer.");
    }
  } catch (retryError) {
    console.warn("[useFairyChat] Leak retry failed, keeping original answer:", retryError);
  }

  return parsed;
};

const inferChartAndKeyword = (text, chartList, keywordsByChart, selectedContext) => {
  const normalizedText = normalizeForMatch(text);
  const explicitChart = extractChartFromText(normalizedText, chartList);
  let chart = explicitChart || selectedContext.chart || null;

  if (!chart) {
    const hits = [];
    Object.keys(keywordsByChart).forEach((chartName) => {
      const match = findKeywordMatch(normalizedText, keywordsByChart[chartName]);
      // Rank on the synonym that actually matched, not the bundle it came from, so a
      // long bundle does not out-rank a more specific hit in another chart.
      if (match) hits.push({ chart: chartName, keyword: match.bundle, score: match.score });
    });

    if (hits.length === 1) {
      return { chart: hits[0].chart, keyword: hits[0].keyword };
    }

    if (hits.length > 1) {
      // Always return the top-ranked hit; on score ties, prefer the first chart in DEFAULT_CHARTS order.
      const rankedHits = [...hits].sort((a, b) => b.score - a.score);
      return { chart: rankedHits[0].chart, keyword: rankedHits[0].keyword };
    }

    return { chart: null, keyword: null };
  }

  const chartKeywords = keywordsByChart[chart] || [];
  const keyword = findKeywordInText(normalizedText, chartKeywords);
  return { chart, keyword };
};

const isCategorySelectionInput = (text, period, chart) => {
  if (!chart) return false;

  const normalizedText = normalizeForMatch(text);
  const labels = Array.from(new Set([chart, ...getChartSynonyms(chart)]));

  return labels.some((label) => {
    const normalizedLabel = normalizeForMatch(label);
    const normalizedPeriodLabel = normalizeForMatch(`${period} ${label}`);
    return normalizedText === normalizedLabel || normalizedText === normalizedPeriodLabel;
  });
};

const pickInitialKeywordForChart = (chart, chartKeywordMap) => {
  if (!chart) return null;

  const available = chartKeywordMap[chart] || [];
  if (available.length === 0) return null;

  const preferred = Array.from(new Set([chart, ...getChartSynonyms(chart)])).map((item) => normalizeForMatch(item));
  const preferredMatch = available.find((keyword) => preferred.includes(normalizeForMatch(keyword)));

  return preferredMatch || available[0];
};

// Scores how well a master's description overlaps with the conversation topic, used as a
// non-biased fallback when the AI matcher is unavailable or returns an unusable response.
const scoreMasterMatch = (master, queryText) => {
  const queryWords = new Set(
    normalizeForMatch(queryText)
      .split(" ")
      .filter((word) => word.length > 2)
  );
  if (queryWords.size === 0) return 0;

  const descriptionWords = normalizeForMatch(master?.description || "").split(" ");
  let score = 0;
  descriptionWords.forEach((word) => {
    if (queryWords.has(word)) score += 1;
  });
  return score;
};

// Ranks every master by topic overlap and returns the top matches (used as a fallback
// when the AI matcher is unavailable, and to cap how many masters we ever suggest at once).
const MAX_SUGGESTED_MASTERS = 4;

const pickMastersHeuristically = (mastersList, userText, chart, keyword, limit = MAX_SUGGESTED_MASTERS) => {
  if (!mastersList || mastersList.length === 0) return [];

  const queryText = [userText, chart, keyword].filter(Boolean).join(" ");
  const ranked = [...mastersList]
    .map((master) => ({ master, score: scoreMasterMatch(master, queryText) }))
    .sort((a, b) => b.score - a.score);

  console.info(
    "[pickMastersHeuristically] Per-master overlap scores (higher = more shared keywords with the question):",
    ranked.map((entry, index) => ({
      id: entry.master.id,
      name: entry.master.name,
      score: entry.score,
      selected: index < limit,
    }))
  );

  return ranked.slice(0, limit).map((entry) => entry.master);
};

// Tolerant JSON parsing for the master-pick response — some models still wrap the
// JSON with stray text even under json_object mode, so fall back to block extraction.
const parseMasterPickResponse = (rawText) => {
  const cleaned = stripCodeFences(rawText);

  const tryParse = (value) => {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  };

  let parsed = tryParse(cleaned);
  if (!parsed) {
    const jsonBlock = cleaned.match(/\{[\s\S]*\}/);
    if (jsonBlock) parsed = tryParse(jsonBlock[0]);
  }

  return parsed;
};

export function useFairyChat() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [keywords, setKeywords] = useState([]);

  const [shopSuggestions, setShopSuggestions] = useState([]);
  const [consultationSuggestions, setConsultationSuggestions] = useState([]);
  const [chatProducts, setChatProducts] = useState([]);
  const [chatCredit, setChatCredit] = useState(null); // questions the user has left
  // null means the balance hasn't loaded yet — only a confirmed 0 locks the chat.
  const outOfCredit = chatCredit != null && chatCredit <= 0;

  const [guidanceConfig, setGuidanceConfig] = useState({
    charts: DEFAULT_CHARTS,
    periods: DEFAULT_PERIODS,
    defaultPeriod: "monthly",
  });
  const [keywordsByChart, setKeywordsByChart] = useState({});

  const [userProfile, setUserProfile] = useState({
    gender: null,
    strengthCategory: null,
    lunarData: null,
    zodiacSign: null,
    chartValues: null,
    chartValuesSource: null,
  });

  const userProfileRef = useRef(userProfile);
  const keywordsByChartRef = useRef({});
  // Separate from keywordsByChartRef on purpose — "general" isn't a real chart
  // (no period, no score), so it's kept out of every chart-keyed assumption downstream
  // (getChartValueByPeriod, greeting labels, shop life-area matching, chart chips).
  const generalKeywordsRef = useRef([]);
  const safetyScenariosRef = useRef([]);
  const profileLoadPromiseRef = useRef(Promise.resolve());
  const selectedContextRef = useRef({ period: "monthly", chart: null });
  const userMessageCountRef = useRef(0);
  const greetedRef = useRef(false); // opening greeting shown once per chat session
  const shopProductsCacheRef = useRef(null); // null = not loaded yet
  const mastersCacheRef = useRef(null); // null = not loaded yet
  const chartApiMapRef = useRef({
    wealth: "wealth",
    health: "health",
    relationship: "relationship",
    career: "career",
    wisdom: "wisdom",
  });

  const appendBotReply = (answer) => {
    setMessages((prev) => [
      ...prev,
      { sender: "bot", text: answer || "I'm here to guide you." },
    ]);
  };

  // AI responses come directly from OpenRouter only.
  const shouldUseOpenRouterDirect = () => AI_ONLY_OPENROUTER_MODE && !!getOpenRouterConfig().apiKey;

  // Opening greeting: one bot bubble speaking to the user's best and worst daily
  // chart area. Silent whenever the daily forecast or both tips are unavailable.
  // Reuses today's cached greeting (SG day) so a reset shows the same text.
  const showOpeningGreeting = async () => {
    const custId = getCustId();
    const language = getUiLanguage();
    const cachedGreeting = readCachedGreeting(custId, language.code);
    if (cachedGreeting) {
      console.log("[useFairyChat] Opening greeting served from cache (same SG day)");
      appendBotReply(cachedGreeting);
      return;
    }

    const profile = userProfileRef.current;
    console.groupCollapsed("[useFairyChat] opening greeting");
    console.log("Daily chart values:", profile.chartValues?.daily);
    console.log("Chart values source:", profile.chartValuesSource);

    const extremes = pickDailyExtremes(profile.chartValues?.daily);
    if (!extremes) {
      console.warn("[useFairyChat] Greeting skipped: no usable daily chart (missing forecast or all zero)");
      console.groupEnd();
      return;
    }

    const langId = parseInt(localStorage.getItem("languageId") || "2224", 10) || 2224;
    const symbol = profile.lunarData?.A2 || "";
    const { highest, lowest } = extremes;

    console.log("Natal symbol (A2):", symbol || "(none)", "| LangId:", langId);
    console.log("Highest daily area:", highest.area, {
      value: highest.value,
      category: getGreetingCategoryLabel(highest.area),
      score: toGreetingScoreBand(highest.value),
    });
    console.log("Lowest daily area:", lowest.area, {
      value: lowest.value,
      category: getGreetingCategoryLabel(lowest.area),
      score: toGreetingScoreBand(lowest.value),
      sameAsHighest: lowest.area === highest.area,
    });

    const requests = [fetchGreetingTip({ area: highest.area, value: highest.value, symbol, langId })];
    if (lowest.area !== highest.area) {
      requests.push(fetchGreetingTip({ area: lowest.area, value: lowest.value, symbol, langId }));
    }

    const tips = (await Promise.all(requests)).map((tip) => tip.trim()).filter(Boolean);
    console.log(`Greeting tips resolved: ${tips.length} of ${requests.length}`, tips);
    if (tips.length === 0) {
      console.warn("[useFairyChat] Greeting skipped: no tip text returned for either area");
      console.groupEnd();
      return;
    }

    const greetingText = await blendGreetingTips({
      openRouterConfig: getOpenRouterConfig(),
      tips,
      language,
      referer: typeof window !== "undefined" ? window.location.origin : "",
    });
    console.log("Greeting bot message:", greetingText);
    console.groupEnd();
    writeCachedGreeting(custId, language.code, greetingText);
    appendBotReply(greetingText);
  };

  useEffect(() => {
    const config = getOpenRouterConfig();
    console.info("[useFairyChat] OpenRouter enabled:", !!config.apiKey);
    console.info("[useFairyChat] OpenRouter config source:", config.source);
    console.info("[useFairyChat] OpenRouter model:", config.model);
    console.info("[useFairyChat] AI-only mode:", AI_ONLY_OPENROUTER_MODE);
  }, []);

  // Fire the opening greeting once the daily chart lands, and only into an empty thread.
  // A greeting already cached for today needs no chart, so it shows straight away.
  useEffect(() => {
    if (greetedRef.current || messages.length > 0) return;
    const hasCachedGreeting = !!readCachedGreeting(getCustId(), getUiLanguage().code);
    if (!hasCachedGreeting && !pickDailyExtremes(userProfile.chartValues?.daily)) return;

    greetedRef.current = true;
    console.log("[useFairyChat] Daily chart landed — triggering opening greeting");
    showOpeningGreeting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile.chartValues]);

  // Re-fire the opening greeting whenever the UI language actually changes, so switching
  // languages mid-chat doesn't leave the very first bubble stuck in the old one. Only
  // re-fires if a greeting was already shown this session — otherwise the chart effect
  // above will fire the (correctly-languaged) greeting once the daily chart lands.
  const greetingLangRef = useRef(getUiLanguage().code);
  useEffect(() => {
    const handleLanguageChanged = () => {
      const nextLangCode = getUiLanguage().code;
      if (nextLangCode === greetingLangRef.current) return;

      greetingLangRef.current = nextLangCode;
      if (!greetedRef.current) return;

      console.log("[useFairyChat] UI language changed — re-firing opening greeting:", nextLangCode);
      showOpeningGreeting();
    };

    i18n.on("languageChanged", handleLanguageChanged);
    return () => i18n.off("languageChanged", handleLanguageChanged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Question packs shown in the chat top-up panel. The API answers 200 even when it
  // rejects a request, so only a real array is treated as a result.
  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        // console.log("[useFairyChat] ListChatProduct request:", LIST_CHAT_PRODUCTS_URL);
        const res = await fetch(LIST_CHAT_PRODUCTS_URL);
        const data = await res.json();
        // console.log("[useFairyChat] ListChatProduct response:", data);
        if (!isMounted) return;
        if (!Array.isArray(data)) {
          console.warn("[useFairyChat] ListChatProduct returned a non-list payload, ignoring it");
          return;
        }

        const packs = data.map(normalizeChatProduct).filter((p) => p.label);
        // console.log("[useFairyChat] Chat product packs:", packs);
        setChatProducts(packs);
      } catch (error) {
        console.error("[useFairyChat] ListChatProduct failed:", error);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  // Crisis-intervention catalog, loaded once so `sendMessage` never has to await it on the
  // hot path. Cache first (instant), then a fresh fetch to pick up any backend edits.
  useEffect(() => {
    let isMounted = true;

    const cached = readCachedSafetyScenarios();
    if (cached.length > 0) {
      safetyScenariosRef.current = cached;
    }

    (async () => {
      try {
        console.log("[useFairyChat] SafetyScenarios request:", GET_SAFETY_SCENARIOS_URL);
        const res = await fetch(GET_SAFETY_SCENARIOS_URL);
        const data = await res.json();
        console.log("[useFairyChat] SafetyScenarios response:", data);
        if (!isMounted) return;

        const scenarios = normalizeSafetyScenariosPayload(data);
        if (scenarios.length === 0) {
          console.warn("[useFairyChat] SafetyScenarios returned no usable rows, keeping cached/previous list");
          return;
        }

        safetyScenariosRef.current = scenarios;
        writeCachedSafetyScenarios(scenarios);
      } catch (error) {
        console.error("[useFairyChat] SafetyScenarios failed:", error);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  // Questions remaining, refreshed whenever one is spent or a pack is bought.
  const refreshChatCredit = async () => {
    const credit = await fetchChatCredit();
    if (credit != null) setChatCredit(credit);
  };

  useEffect(() => {
    let isMounted = true;

    fetchChatCredit().then((credit) => {
      if (isMounted && credit != null) setChatCredit(credit);
    });

    return () => {
      isMounted = false;
    };
  }, []);

  const buildCategoryPrompt = (period, chart) => `${period} ${chart}`.trim();

  const appendKeywordSuggestions = (period, chart, chartKeywordMap) => {
    const available = chartKeywordMap[chart] || [];
    if (available.length === 0) {
      appendBotReply(i18n.t("chat.noKeywords", { period, chart }));
      setSuggestions([]);
      return;
    }

    appendBotReply(i18n.t("chat.pickKeyword"));

    // Some keyword entries bundle several synonyms into one comma-separated string
    // (e.g. "boss problem, micromanager, overbearing boss, toxic boss, difficult boss").
    // Show each synonym as its own chip, but keep the full original string as the
    // query/keyword sent to the guidance answer lookup so it still matches the backend row.
    const chips = [];
    available.slice(0, 16).forEach((keyword) => {
      const labels = keyword
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean);
      const query = `${buildCategoryPrompt(period, chart)} ${keyword}`;

      (labels.length > 0 ? labels : [keyword]).forEach((label) => {
        chips.push({ id: null, text: label, query });
      });
    });

    setSuggestions(chips);
  };

  const fetchKeywordsForChart = async (chart, customMap = null) => {
    const activeMap = customMap || chartApiMapRef.current;
    const tokens = [activeMap[chart], chart, ...getChartSynonyms(chart)]
      .map((token) => normalizeForMatch(token))
      .filter(Boolean);

    const dedupedTokens = Array.from(new Set(tokens));
    for (const token of dedupedTokens) {
      try {
        const response = await fetch(`${GET_GUIDANCE_KEYWORDS_URL}?chart=${encodeURIComponent(token)}`);
        if (!response.ok) continue;

        const data = await response.json();
        if (!Array.isArray(data)) continue;

        return dedupeKeywords(data);
      } catch {
        // Continue to fallback token.
      }
    }

    return [];
  };

  const fetchAllKeywords = async (charts = DEFAULT_CHARTS) => {
    try {
      const response = await fetch(GET_GUIDANCE_KEYWORDS_ALL_URL);
      if (!response.ok) return {};

      const data = await response.json();

      const generalList = extractGeneralKeywordsPayload(data);
      if (generalList.length > 0) {
        generalKeywordsRef.current = dedupeKeywords([...generalKeywordsRef.current, ...generalList]);
        writeCachedGeneralKeywords(generalKeywordsRef.current);
      }

      return normalizeKeywordsPayload(data, charts);
    } catch {
      return {};
    }
  };

  const syncKeywordState = (keywordMap, chartList = guidanceConfig.charts) => {
    const charts = chartList?.length > 0 ? chartList : DEFAULT_CHARTS;
    const normalizedMap = mergeKeywordMaps({}, keywordMap, charts);

    keywordsByChartRef.current = normalizedMap;
    setKeywordsByChart(normalizedMap);
    setKeywords(dedupeKeywords(Object.values(normalizedMap).flat()).slice(0, 40));
    writeCachedKeywords(normalizedMap, charts);
  };

  const ensureAllChartKeywords = async (chartList = guidanceConfig.charts) => {
    const charts = chartList?.length > 0 ? chartList : DEFAULT_CHARTS;
    let activeMap = keywordsByChartRef.current;

    // Fill keywords in this order: in-memory state -> local cache -> bulk API -> per-chart API.
    const hasAnyLoadedKeywords = Object.values(activeMap).some((values) => Array.isArray(values) && values.length > 0);
    if (!hasAnyLoadedKeywords) {
      const cachedKeywords = readCachedKeywords(charts);
      if (Object.keys(cachedKeywords).length > 0) {
        activeMap = mergeKeywordMaps(activeMap, cachedKeywords, charts);
      }

      const missingAfterCache = charts.filter((chart) => !Array.isArray(activeMap[chart]) || activeMap[chart].length === 0);
      if (missingAfterCache.length > 0) {
        const allKeywords = await fetchAllKeywords(charts);
        if (Object.keys(allKeywords).length > 0) {
          activeMap = mergeKeywordMaps(activeMap, allKeywords, charts);
        }
      }
    }

    const missingCharts = charts.filter((chart) => !Array.isArray(activeMap[chart]) || activeMap[chart].length === 0);
    if (missingCharts.length > 0) {
      const loadedEntries = await Promise.all(
        missingCharts.map(async (chart) => [chart, await fetchKeywordsForChart(chart)])
      );

      const completedMap = { ...activeMap };
      loadedEntries.forEach(([chart, values]) => {
        completedMap[chart] = values;
      });
      activeMap = completedMap;
    }

    if (activeMap !== keywordsByChartRef.current) {
      syncKeywordState(activeMap, charts);
    }

    return activeMap;
  };

  useEffect(() => {
    const cachedKeywords = readCachedKeywords(DEFAULT_CHARTS);
    if (Object.keys(cachedKeywords).length === 0) return;

    syncKeywordState(cachedKeywords, DEFAULT_CHARTS);
  }, []);

  useEffect(() => {
    generalKeywordsRef.current = readCachedGeneralKeywords();
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadGuidanceData = async () => {
      let charts = DEFAULT_CHARTS;
      let periods = DEFAULT_PERIODS;
      let defaultPeriod = "monthly";
      const apiMap = { ...chartApiMapRef.current };

      try {
        const categoriesRes = await fetch(GET_GUIDANCE_CATEGORIES_URL);
        if (categoriesRes.ok) {
          const data = await categoriesRes.json();

          const rawCharts = Array.isArray(data?.Charts) ? data.Charts : Array.isArray(data?.charts) ? data.charts : DEFAULT_CHARTS;
          const rawPeriods = Array.isArray(data?.Periods) ? data.Periods : Array.isArray(data?.periods) ? data.periods : DEFAULT_PERIODS;
          const rawDefaultPeriod = (data?.DefaultPeriod || data?.defaultPeriod || "monthly").toString().toLowerCase();

          const normalizedCharts = dedupeKeywords(rawCharts.map((chart) => normalizeChartName(chart)));
          if (normalizedCharts.length > 0) charts = normalizedCharts;

          const normalizedPeriods = dedupeKeywords(rawPeriods);
          if (normalizedPeriods.length > 0) periods = normalizedPeriods;

          defaultPeriod = periods.includes(rawDefaultPeriod) ? rawDefaultPeriod : (periods.includes("monthly") ? "monthly" : periods[0]);

          rawCharts.forEach((rawChart) => {
            const normalized = normalizeChartName(rawChart);
            if (normalized) apiMap[normalized] = String(rawChart).toLowerCase();
          });
        }
      } catch {
        // Keep defaults when categories endpoint is unavailable.
      }

      chartApiMapRef.current = apiMap;

      let fetchedKeywords = mergeKeywordMaps({}, readCachedKeywords(charts), charts);
      const missingFromCache = charts.filter((chart) => !Array.isArray(fetchedKeywords[chart]) || fetchedKeywords[chart].length === 0);
      // The "general" bucket rides along in the /keywords/all response (captured as a side
      // effect inside fetchAllKeywords) but isn't chart-keyed, so a warm per-chart cache would
      // otherwise skip this fetch and leave generalKeywordsRef empty indefinitely.
      const needsGeneralKeywords = generalKeywordsRef.current.length === 0;

      if (missingFromCache.length > 0 || needsGeneralKeywords) {
        const allKeywords = await fetchAllKeywords(charts);
        if (Object.keys(allKeywords).length > 0) {
          fetchedKeywords = mergeKeywordMaps(fetchedKeywords, allKeywords, charts);
        }
      }

      const missingCharts = charts.filter((chart) => !Array.isArray(fetchedKeywords[chart]) || fetchedKeywords[chart].length === 0);

      if (missingCharts.length > 0) {
        const fallbackEntries = await Promise.all(
          missingCharts.map(async (chart) => [chart, await fetchKeywordsForChart(chart, apiMap)])
        );

        const fallbackMap = { ...fetchedKeywords };
        fallbackEntries.forEach(([chart, chartKeywords]) => {
          fallbackMap[chart] = chartKeywords;
        });
        fetchedKeywords = fallbackMap;
      }

      if (!isMounted) return;

      setGuidanceConfig({ charts, periods, defaultPeriod });
      syncKeywordState(fetchedKeywords, charts);
      selectedContextRef.current = {
        period: defaultPeriod,
        chart: null,
      };
    };

    loadGuidanceData();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const userName = localStorage.getItem("username");
    const userID = localStorage.getItem("userID");
    if (!userName || !userID) {
      return () => {
        isMounted = false;
      };
    }

    const cachedProfile = readCachedProfile(userID);
    if (cachedProfile) {
      userProfileRef.current = cachedProfile;
      setUserProfile(cachedProfile);
    }

    const loadUserProfile = async () => {
      try {
        const profileRes = await fetch(GET_PROFILE_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            UserName: userName,
            ID: userID,
            UserToken: "",
            Token: { AccessToken: API_TOKEN },
          }),
        });

        if (!profileRes.ok) return;

        const profileData = await profileRes.json();
        const profile = profileData?.Profiles?.[0];
        // console.log("[useFairyChat] Fetched user profile:", profile);
        if (!profile) return;

        const gender = profile.Gender;
        const dob = profile.DOB;
        if (!dob) return;

        const bd = parseDobLikeNatalChart(dob);
        if (!bd) return;

        const y = bd.getFullYear();
        const mo = String(bd.getMonth() + 1).padStart(2, "0");
        const d = String(bd.getDate()).padStart(2, "0");
        const time = `${String(bd.getHours()).padStart(2, "0")}:${String(bd.getMinutes()).padStart(2, "0")}`;

        const monthRaw = bd.getMonth() + 1;
        const dayRaw = bd.getDate();
        const uid = userID || "0";

        // Start forecast fetch early so profile + stems calls can run in parallel.
        const forecastPromise = (async () => {
          const fetchForecastJson = async (url) => {
            const response = await fetch(url);
            if (!response.ok) return null;
            return response.json();
          };

          const cGender = gender?.toLowerCase() === "female" ? 1 : 0;
          const encodedTime = encodeURIComponent(time);
          const forecastUrls = [
            `${GET_NATAL_DAILY_FORECAST_URL}?cYear=${y}&cMonth=${monthRaw}&cDay=${dayRaw}&cTime=${encodedTime}&cGender=${cGender}&CustID=${uid}`,
            `${GET_NATAL_DAILY_FORECAST_URL}?cYear=${y}&cMonth=${monthRaw}&cDay=${dayRaw}&cTime=${encodedTime}&cGender=${cGender}`,
            `${GET_NATAL_DAILY_FORECAST_URL}?cYear=${y}&cMonth=${monthRaw}&cDay=${dayRaw}&cGender=${cGender}`,
          ];

          let forecastData = null;
          for (const forecastUrl of forecastUrls) {
            forecastData = await fetchForecastJson(forecastUrl);
            if (hasForecastTables(forecastData)) break;
          }

          if (!hasForecastTables(forecastData)) return null;

          const yearlyValues = normalizeChartValues(forecastData.Table?.[0] || null);
          const monthlyValues = normalizeChartValues(forecastData.Table1?.[0] || null);
          const dailyValues = normalizeChartValues(forecastData.Table2?.[0] || null);

          return {
            yearlyValues,
            monthlyValues,
            dailyValues,
          };
        })();

        const langId = parseInt(localStorage.getItem("languageId") || "2224", 10) || 2224;
        const numericUserId = parseInt(userID, 10) || 0;

        const [chineseResult, stemsResult] = await Promise.allSettled([
          fetch(`${GET_CHINESE_DOB_URL}?cYear=${y}&cMonth=${mo}&cDay=${d}&cTime=${time}`).then((response) => (
            response.ok ? response.json() : null
          )),
          (async () => {
            const stemResponse = await fetch(GET_LUNAR_STEM_URL, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                cYear: y,
                cMonth: Number(mo),
                cDay: Number(d),
                cTime: time,
                CustID: numericUserId,
                LangId: langId,
              }),
            });

            if (stemResponse.ok) {
              return stemResponse.json();
            }

            const fallbackResponse = await fetch(`${GET_LUNAR_STEM_URL}?cYear=${y}&cMonth=${mo}&cDay=${d}&cTime=${time}`);
            return fallbackResponse.ok ? fallbackResponse.json() : null;
          })(),
        ]);

        const chineseData = chineseResult.status === "fulfilled" ? chineseResult.value : null;
        const stemsRaw = stemsResult.status === "fulfilled" ? stemsResult.value : null;

        if (!Array.isArray(chineseData) || !chineseData[0]) return;

        const chineseRow = chineseData[0];
        const strengthRaw = chineseRow.CHART_STRENGHT;
        const strengthCategory = strengthRaw?.toLowerCase() === "strong" ? "strong" : "weak";

        const lunarData = {
          A1: chineseRow.A1 || null,
          A2: chineseRow.A2 || null,
          A3: chineseRow.A3 || null,
          A4: chineseRow.A4 || null,
          B1: chineseRow.B1 || null,
          B2: chineseRow.B2 || null,
          B3: chineseRow.B3 || null,
          B4: chineseRow.B4 || null,
        };

        let zodiacSign = chineseRow.ZODIAC || null;
        let stemsRow = null;
        if (stemsRaw?.Tables?.length > 0) {
          stemsRow = stemsRaw.Tables[0]?.[0] || null;
        } else if (Array.isArray(stemsRaw) && stemsRaw.length > 0) {
          stemsRow = stemsRaw[0];
        }

        if (!lunarData.A2) {
          lunarData.A2 = stemsRow?.NATAL || stemsRow?.B1 || null;
        }
        zodiacSign = zodiacSign || stemsRow?.ZODIAC || null;

        const stemsFallback = normalizeChartValues(stemsRow);
        let nextProfile = {
          gender,
          strengthCategory,
          lunarData,
          zodiacSign,
          chartValues: stemsFallback
            ? {
                daily: stemsFallback,
                monthly: stemsFallback,
                yearly: stemsFallback,
                natal: stemsFallback,
              }
            : null,
          chartValuesSource: stemsFallback ? "stems" : null,
        };

        if (!isMounted) return;

        // Don't downgrade chart values if we already have forecast-quality data from cache.
        const alreadyHasForecast = userProfileRef.current.chartValuesSource === "forecast";
        if (alreadyHasForecast) {
          nextProfile = {
            ...nextProfile,
            chartValues: userProfileRef.current.chartValues,
            chartValuesSource: "forecast",
          };
        }

        userProfileRef.current = nextProfile;
        setUserProfile(nextProfile);
        writeCachedProfile(userID, nextProfile);

        try {
          const forecastValues = await forecastPromise;
          if (!forecastValues || !isMounted) return;

          const { yearlyValues, monthlyValues, dailyValues } = forecastValues;
          const hasForecast = yearlyValues || monthlyValues || dailyValues;
          if (!hasForecast) return;

          nextProfile = {
            ...nextProfile,
            chartValues: {
              daily: dailyValues || monthlyValues || yearlyValues || stemsFallback,
              monthly: monthlyValues || dailyValues || yearlyValues || stemsFallback,
              yearly: yearlyValues || monthlyValues || dailyValues || stemsFallback,
              natal: stemsFallback || nextProfile.chartValues?.natal || null,
            },
            chartValuesSource: "forecast",
          };

          userProfileRef.current = nextProfile;
          setUserProfile(nextProfile);
          writeCachedProfile(userID, nextProfile);
        } catch {
          // Keep fallback chart values.
        }
      } catch {
        // Skip profile personalization if any profile endpoint fails.
      }
    };

    const pendingLoad = loadUserProfile();
    profileLoadPromiseRef.current = pendingLoad;

    return () => {
      isMounted = false;
    };
  }, []);

  const fetchShopSuggestionsForChart = async (chart, period = null) => {
    const langId = parseInt(localStorage.getItem("languageId") || "2224", 10) || 2224;

    if (!shopProductsCacheRef.current) {
      try {
        const res = await fetch(`${GET_PRODUCTS_URL}?LangId=${langId}`);
        if (!res.ok) return [];
        const data = await res.json();
        if (!Array.isArray(data)) return [];
        shopProductsCacheRef.current = data.map((item) => ({
          id: item.ID,
          name: (
            item.DESC_OTH ||
            item.DISPLAY_NAME ||
            item.FILENAME ||
            item.NAME ||
            item.TITLE ||
            item.PRODUCT_NAME ||
            item.NAME_ENG ||
            item.DESCRIPTION ||
            ""
          ),
          image: (
            item.TUMB_URL ||
            item.ThumbFile ||
            item.ImageUrl ||
            item.IMAGE_URL ||
            item.IMAGE ||
            item.ThumbUrl ||
            ""
          ),
          price: typeof item.CASH_AMT === "number" ? item.CASH_AMT : Number(item.CASH_AMT) || 0,
          charm: item.OTHER_AMT || 0,
          categoryDesc: item.CATEGORY_DESC || "",
          period: extractPeriodFromDescEng(item.DESC_ENG),
          lifeAreas: readProductLifeAreas(item),
        }));
      } catch {
        return [];
      }
    }

    if (!chart || !shopProductsCacheRef.current || shopProductsCacheRef.current.length === 0) return [];

    // Filter by LIFE_AREAS matching the conversation chart
    const matchTerms = getLifeAreaMatchTerms(chart);
    let matched = shopProductsCacheRef.current.filter((p) =>
      p.lifeAreas.some((area) => matchTerms.some((term) => area.includes(term)))
    );

    if (matched.length === 0) {
      console.warn(
        `[fetchShopSuggestionsForChart] No products carry life area "${chart}" (matched on: ${matchTerms.join(", ")}).`,
        `Catalog size: ${shopProductsCacheRef.current.length}.`,
        "Check that the product API returns LIFE_AREAS / LIFE_ARES / LIST_LIFE_AREAS."
      );
    }

    // Narrow further by period derived from DESC_ENG; fall back to chart-only if nothing matches
    if (period) {
      const periodMatched = matched.filter((p) => !p.period || p.period === period);
      if (periodMatched.length > 0) matched = periodMatched;
    }

    return matched.slice(0, 3);
  };

  const fetchMastersList = async () => {
    if (mastersCacheRef.current) return mastersCacheRef.current;

    const langId = parseInt(localStorage.getItem("languageId") || "2224", 10) || 2224;
    const userId = localStorage.getItem("userID") || localStorage.getItem("userId") || "";

    try {
      const res = await fetch(`${GET_MASTERS_URL}?CustId=${userId}&LangId=${langId}`);
      if (!res.ok) return [];

      const data = await res.json();
      if (!Array.isArray(data)) return [];

      const mapped = data
        .filter((p) => p.DisplayName && p.Status === 0)
        .map((p) => {
          let photo = null;
          if (p.Photo && p.Photo !== "null") {
            const trimmed = String(p.Photo).trim();
            if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
              photo = trimmed;
            } else if (trimmed.startsWith("/")) {
              photo = `${MASTER_BASE_URL}${trimmed}`;
            } else {
              photo = `${MASTER_BASE_URL}/${trimmed}`;
            }
          }

          return {
            id: p.ID,
            name: p.DisplayName,
            image: photo,
            description: p.Address || "",
            rate: p.Rate !== null && p.Rate !== undefined ? Number(p.Rate) : 0,
            gemRate: p.Gems !== null && p.Gems !== undefined ? Number(p.Gems) : 0,
          };
        });

      console.info(
        `[fetchMastersList] API returned ${data.length} total, ${mapped.length} eligible (Status === 0):`,
        mapped.map((m) => ({ id: m.id, name: m.name }))
      );

      mastersCacheRef.current = mapped;
      return mapped;
    } catch {
      return [];
    }
  };

  // Ask OpenRouter to pick the master(s) whose description/specialty best fit the
  // client's question, so the consultation suggestion(s) feel personally matched.
  const pickMastersWithOpenRouter = async ({ mastersList, userText, chart, keyword }) => {
    if (!mastersList || mastersList.length === 0) return [];

    console.info("[pickMastersWithOpenRouter] Candidate masters:", mastersList.map((m) => ({ id: m.id, name: m.name })));

    if (mastersList.length === 1) return [mastersList[0]];

    const openRouterConfig = getOpenRouterConfig();
    if (!openRouterConfig.apiKey) {
      console.warn("[pickMastersWithOpenRouter] No OpenRouter API key; using heuristic match.");
      return pickMastersHeuristically(mastersList, userText, chart, keyword);
    }

    const masterSummaries = mastersList.map((m) => ({
      id: m.id,
      name: m.name,
      description: (m.description || "").slice(0, 400),
    }));

    const responseShapeHint = '{"masters": [{"masterId": "<id copied from the list>", "reason": "<short reason>"}]}';

    const payload = {
      model: openRouterConfig.model,
      temperature: 0.2,
      reasoning: { effort: "none" },
      response_format: { type: "json_object" },
      messages: [
        {
          role: "system",
          content: [
            "You match a client's question to the most suitable metaphysics master(s) for a paid consultation.",
            "You will be given a list of masters with their id, name, and description/specialty.",
            "Evaluate EVERY master individually against the client's topic and chart \u2014 do not stop once you find one good match.",
            `Include every master whose description reasonably relates to the topic (shared themes like wealth, career, relationships, health, investment, success, harmony, etc.), ranked best match first, up to ${MAX_SUGGESTED_MASTERS} total.`,
            "Most questions genuinely relate to more than one master's description, so only return a single master when the others are clearly unrelated to the topic.",
            "Do not default to the same master every time \u2014 base the decision only on how well each specific description matches this specific question.",
            "Every masterId you return MUST be exactly one of the id values provided in the masters list, copied verbatim.",
            `Return strict JSON only in this shape: ${responseShapeHint}`,
            "No markdown fences. No extra text.",
          ].join(" "),
        },
        {
          role: "user",
          content: `Client question: ${userText}\nTopic chart: ${chart || "unspecified"}\nKeyword: ${keyword || "unspecified"}\n\nMasters:\n${JSON.stringify(masterSummaries, null, 2)}`,
        },
      ],
    };

    try {
      const referer = typeof window !== "undefined" ? window.location.origin : "";
      const response = await fetch(OPENROUTER_CHAT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${openRouterConfig.apiKey}`,
          "HTTP-Referer": referer,
          "X-Title": openRouterConfig.appName,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        console.warn("[pickMastersWithOpenRouter] OpenRouter request failed:", response.status);
        return pickMastersHeuristically(mastersList, userText, chart, keyword);
      }

      const data = await response.json();
      const content = getOpenRouterContentText(data);
      const parsed = parseMasterPickResponse(content);
      console.info("[pickMastersWithOpenRouter] Raw response:", content);
      console.info("[pickMastersWithOpenRouter] Parsed pick:", parsed);

      const rawPicks = Array.isArray(parsed?.masters)
        ? parsed.masters
        : (parsed?.masterId ? [{ masterId: parsed.masterId, reason: parsed.reason }] : []);

      const seenIds = new Set();
      const matches = [];
      rawPicks.forEach((pick) => {
        if (!pick || matches.length >= MAX_SUGGESTED_MASTERS) return;

        let matched = mastersList.find((m) => String(m.id) === String(pick.masterId));
        // Some models occasionally return a name instead of the id despite instructions.
        if (!matched && pick.masterId) {
          matched = mastersList.find((m) => normalizeForMatch(m.name) === normalizeForMatch(String(pick.masterId)));
        }
        if (!matched || seenIds.has(matched.id)) return;

        seenIds.add(matched.id);
        matches.push({ ...matched, matchReason: pick.reason || null });
      });

      // Break down how EVERY candidate master was evaluated, not just the ones the AI picked,
      // so it's clear whether a master was included, excluded, or dropped for being invalid/duplicate.
      console.info(
        "[pickMastersWithOpenRouter] Per-master evaluation:",
        mastersList.map((master) => {
          const pick = rawPicks.find((p) => {
            if (!p) return false;
            if (String(p.masterId) === String(master.id)) return true;
            return normalizeForMatch(String(p.masterId || "")) === normalizeForMatch(master.name);
          });
          const wasIncluded = matches.some((m) => m.id === master.id);

          return {
            id: master.id,
            name: master.name,
            includedInResponse: !!pick,
            includedInFinalPick: wasIncluded,
            reason: pick?.reason || (wasIncluded ? null : "not selected by AI"),
          };
        })
      );

      if (matches.length === 0) {
        console.warn("[pickMastersWithOpenRouter] AI pick unusable; using heuristic match instead.");
        return pickMastersHeuristically(mastersList, userText, chart, keyword);
      }

      return matches;
    } catch (error) {
      console.warn("[pickMastersWithOpenRouter] Request error; using heuristic match.", error);
      return pickMastersHeuristically(mastersList, userText, chart, keyword);
    }
  };

  const fetchConsultationSuggestion = async (chart, userText, keyword) => {
    const mastersList = await fetchMastersList();
    if (!mastersList || mastersList.length === 0) return [];

    // AI-based matching disabled for now: descriptions overlap enough that the
    // model ends up suggesting nearly every master anyway, so this just adds
    // latency without meaningfully improving the picks. Using the heuristic
    // matcher directly is faster; re-enable pickMastersWithOpenRouter below if needed.
    return pickMastersHeuristically(mastersList, userText, chart, keyword);
    // return pickMastersWithOpenRouter({ mastersList, userText, chart, keyword });
  };

  const ensureChartKeywords = async (chart) => {
    if (keywordsByChartRef.current[chart] && keywordsByChartRef.current[chart].length > 0) {
      return keywordsByChartRef.current;
    }

    const cachedChartKeywords = readCachedKeywords(guidanceConfig.charts)[chart] || [];
    if (cachedChartKeywords.length > 0) {
      const cachedMap = { ...keywordsByChartRef.current, [chart]: cachedChartKeywords };
      syncKeywordState(cachedMap, guidanceConfig.charts);
      return cachedMap;
    }

    const loaded = await fetchKeywordsForChart(chart);
    const updated = { ...keywordsByChartRef.current, [chart]: loaded };
    syncKeywordState(updated, guidanceConfig.charts);
    return updated;
  };

  // Called when the user taps a category tile (Wealth/Career/etc). Lists that
  // chart's keywords as suggestion chips instead of jumping straight to an AI answer.
  const selectCategory = async (chartKey, label = null) => {
    const chart = normalizeChartName(chartKey);
    if (!chart) return;
    // A category pick answers like any other question, so it is locked with the rest.
    if (outOfCredit) {
      console.warn("[useFairyChat] selectCategory blocked: no chat credit left");
      return;
    }

    setSuggestions([]);
    setMessages((prev) => [...prev, { sender: "user", text: label || chart }]);

    const period = selectedContextRef.current.period || guidanceConfig.defaultPeriod || "monthly";
    selectedContextRef.current = { period, chart };

    setLoading(true);
    try {
      const keywordMap = await ensureChartKeywords(chart);
      appendKeywordSuggestions(period, chart, keywordMap);
    } finally {
      setLoading(false);
    }
  };

  // Buys `qty` of a ListChatProduct pack. PayMode: 0 pays with gems, 1 with a card —
  // the card charge itself happens in Stripe first, this only records the purchase.
  // The API answers 200 even when it refuses, so success is read off the payload.
  const purchaseChatProduct = async ({ id, qty = 1, payMode = 0 }) => {
    const custId = getCustId();
    if (!custId) {
      console.warn("[useFairyChat] PurchaseChat skipped: no userID in localStorage");
      return { successful: false, message: i18n.t("chat.purchase.signInRequired") };
    }
    if (!id) {
      console.warn("[useFairyChat] PurchaseChat skipped: no pack id");
      return { successful: false, message: i18n.t("chat.purchase.packUnavailable") };
    }

    const units = Number(qty) || 1;
    console.groupCollapsed("[useFairyChat] purchaseChatProduct");
    // console.log("Pack id:", id, "Qty:", units, "PayMode:", payMode, payMode === 0 ? "(gems)" : "(card)");

    try {
      // Gem purchases are checked against the balance first — the API would otherwise
      // refuse with a message the user can do less with.
      if (payMode === 0) {
        const pack = chatProducts.find((p) => p.id === id);
        const requiredGems = (pack?.gem || 0) * units;
        // console.log("Gems required:", requiredGems);

        if (requiredGems > 0) {
          const availableGems = await fetchGemBalance();
          if (availableGems < requiredGems) {
            console.warn("[useFairyChat] Insufficient gems:", { requiredGems, availableGems });
            return {
              successful: false,
              insufficientGems: true,
              requiredGems,
              availableGems,
              message: i18n.t("chat.purchase.insufficientGemsMessage", {
                required: requiredGems,
                available: availableGems,
              }),
            };
          }
        }
      }

      const payload = [
        {
          CustId: custId,
          Id: id,
          Qty: units,
          PayMode: payMode,
        },
      ];
      // console.log("PurchaseChat request:", payload);

      const res = await fetch(PURCHASE_CHAT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      // console.log("PurchaseChat response:", data);

      const successful = data?.successful === true;
      if (successful) {
        refreshChatCredit();
      } else {
        console.warn("[useFairyChat] PurchaseChat did not report success:", data);
      }

      return {
        successful,
        message: data?.Message || data?.message || "",
        raw: data,
      };
    } catch (error) {
      console.error("[useFairyChat] PurchaseChat failed:", error);
      return { successful: false, message: i18n.t("chat.purchase.serviceUnreachable") };
    } finally {
      console.groupEnd();
    }
  };

  // Records the question against the customer server-side. Fire-and-forget: a logging
  // failure must not hold up the answer the user is waiting on. The remaining-questions
  // count is re-read once the write lands, since this is what draws it down.
  const recordUserChat = (question) => {
    const custId = getCustId();
    const trimmed = String(question || "").trim();
    if (!custId || !trimmed) {
      console.warn("[useFairyChat] SetUserChat skipped:", { custId, question: trimmed });
      return;
    }

    const payload = { CustId: custId, Question: trimmed };
    // console.log("[useFairyChat] SetUserChat request:", payload);

    fetch(SET_USER_CHAT_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then(async (res) => {
        let body = null;
        try {
          body = await res.json();
        } catch {
          // Some writes answer with an empty body — nothing to report.
        }
        // console.log("[useFairyChat] SetUserChat response:", res.status, body);
        return refreshChatCredit();
      })
      .catch((error) => {
        console.error("[useFairyChat] SetUserChat failed:", error);
      });
  };

  // Main orchestrator: infer period/chart/keyword, then route to OpenRouter or guidance answer API.
  const sendMessage = async (text, _questionId = null, displayText = text, options = {}) => {
    if (!text.trim()) return;

    // Crisis check runs before anything else — ahead of the credit gate too, so it's never
    // blocked by an empty balance — and answers directly from the scenario's own canned,
    // per-language text instead of spending a question or calling the AI.
    let safetyMatch = findSafetyScenarioMatch(text, safetyScenariosRef.current);

    // Render the user's own bubble now, before anything that needs a network round trip
    // (the AI-based safety escalation below, then translation/guidance later). Only the
    // literal keyword check above — synchronous, no await — has run so far, so there is
    // no reason the message should sit invisible while those requests are in flight.
    setSuggestions([]);
    setMessages((prev) => [...prev, { sender: "user", text: displayText }]);
    // The typing indicator reads off `loading`, so it needs to flip on here too — otherwise
    // it stays hidden through the same safety-check network round trip the bubble used to.
    setLoading(true);

    // A past safety-scenario exchange (the crisis message + canned response) is excluded from
    // every AI call's history from here on — otherwise a genuine self-harm turn keeps
    // "anchoring" later, unrelated messages back into crisis mode via the contextual safety
    // classifier's own deliberately-biased "read together with history" rule (it's built to
    // catch a bare "yes" answering a crisis question, which also means an unrelated follow-up
    // sent right after gets swept in just for sharing that same nearby history).
    const aiHistory = messages.filter((message) => !message.safety);

    // Literal matching only catches this message's own wording — it can't catch a paraphrase,
    // or a bare "yes" answering a crisis question the advisor just asked. Only worth the round
    // trip once the free literal check has already come back empty.
    if (!safetyMatch && shouldUseOpenRouterDirect() && safetyScenariosRef.current.length > 0) {
      safetyMatch = await resolveSafetyScenarioWithOpenRouter({
        openRouterConfig: getOpenRouterConfig(),
        userText: text,
        history: aiHistory,
        scenarios: safetyScenariosRef.current,
        referer: typeof window !== "undefined" ? window.location.origin : "",
      });
      if (safetyMatch) {
        console.warn("[useFairyChat] Safety scenario matched (AI, contextual):", safetyMatch.scenario);
      }
    }
    if (safetyMatch) {
      const langId = parseInt(localStorage.getItem("languageId") || "2224", 10) || 2224;
      const safetyResponse = getSafetyScenarioResponseText(safetyMatch, langId);
      console.warn("[useFairyChat] Safety scenario matched:", safetyMatch.scenario, "| langId:", langId);

      setMessages((prev) => {
        const updated = [...prev];
        // Tag the user bubble just pushed above too, so both sides of this exchange are
        // excluded from history together next time.
        if (updated.length > 0) {
          updated[updated.length - 1] = { ...updated[updated.length - 1], safety: true };
        }
        updated.push({
          sender: "bot",
          text: safetyResponse || "I want to pause for a moment — please reach out to a crisis support line near you.",
          safety: true,
        });
        return updated;
      });
      setLoading(false);
      return;
    }

    // The allowance is spent. The UI blocks this too, but chips and Enter reach here
    // directly, so the refusal also sits at the single point that draws the balance down.
    if (outOfCredit) {
      console.warn("[useFairyChat] sendMessage blocked: no chat credit left");
      setLoading(false);
      return;
    }

    const isInitialInteraction = messages.length === 0;
    // AI follow-up chips carry the chart their answer was written for. A chip is a
    // continuation of that answer, so its wording must not re-route the reading —
    // "Health sciences" picked from a career follow-up is still a career question.
    // Typed questions pass nothing here and keep inferring the chart from the text.
    const lockedChart = guidanceConfig.charts?.includes(options?.contextChart)
      ? options.contextChart
      : null;

    console.groupCollapsed("[useFairyChat] sendMessage");
    console.log("Input text:", text);
    console.log("Display text:", displayText);
    console.log("Initial interaction:", isInitialInteraction);
    console.log("Suggestion chart lock:", lockedChart || "(none — free-text input)");

    userMessageCountRef.current += 1;

    try {
      const directMode = shouldUseOpenRouterDirect();
      if (!directMode) {
        appendBotReply("OpenRouter API key is not configured. Set REACT_APP_OPENROUTER_API_KEY and try again.");
        return;
      }

      // Logged the moment we commit to answering, so it lands alongside (never after)
      // the OpenRouter request rather than waiting on the reply.
      recordUserChat(displayText || text);

      // Period/chart/keyword matching all run on English tokens, and the keyword that ends up
      // posted to GET_GUIDANCE_ANSWER_URL has to be an English catalog bundle. So a question
      // typed in another UI language is translated to English for matching only — `matchText`
      // never reaches the UI, and the AI still sees the user's original wording.
      const uiLanguage = getUiLanguage();
      const isChipInput = (displayText || text) !== text;
      let matchText = text;

      // Logged unconditionally: when this resolves to "en" nothing below runs, and the silence
      // is exactly what makes a wrong-language answer hard to spot in the console.
      // console.log("Answer language:", uiLanguage.code, {
      //   i18n: i18n?.language,
      //   topBarOverride: sessionStorage.getItem("topBarLangOverride"),
      //   storage: localStorage.getItem("language"),
      //   languageId: localStorage.getItem("languageId"),
      // });

      if (uiLanguage.code !== "en" && !isChipInput) {
        const translated = await translateQuestionToEnglish({
          openRouterConfig: getOpenRouterConfig(),
          userText: text,
          sourceLanguageName: uiLanguage.name,
          referer: typeof window !== "undefined" ? window.location.origin : "",
        });
        if (translated) matchText = translated;
        // console.log(`Match text (${uiLanguage.code} -> en):`, matchText, translated ? "" : "(translation failed, using original)");
      }

      // `matchText` (not displayText) carries the "<period> <chart>" prefix injected by keyword
      // chips, so explicit-period detection works for both typed questions and chip clicks. The
      // prefix wins over free-text detection, otherwise a keyword such as "today's plan" would
      // flip the period away from the one the chip was built for.
      const normalizedTextForPeriod = normalizeForMatch(matchText);
      const chipPeriodPrefix = guidanceConfig.periods.find(
        (period) => normalizedTextForPeriod.startsWith(`${normalizeForMatch(period)} `)
      ) || null;
      const explicitPeriod = chipPeriodPrefix || getExplicitPeriod(matchText);
      const fallbackPeriod = isInitialInteraction
        ? (explicitPeriod || "monthly")
        : (explicitPeriod || selectedContextRef.current.period || guidanceConfig.defaultPeriod || "monthly");
      const normalizedPeriod = guidanceConfig.periods.includes(fallbackPeriod)
        ? fallbackPeriod
        : "monthly";
      // When the period is already known — typed by the user or carried by a chip — the AI
      // classifier must not override it. Unspecified periods keep the selected/default period.
      const periodIsExplicit = !!explicitPeriod || isChipInput;

      let activeKeywordMap = keywordsByChartRef.current;
      let inferredChart = null;
      let inferredKeyword = null;

      if (directMode) {
        inferredChart = lockedChart
          || extractChartFromText(matchText, guidanceConfig.charts)
          || selectedContextRef.current.chart
          || null;
        // In AI mode, also attempt keyword inference to enable guidance-seeded answers.
        let kwMap = keywordsByChartRef.current;
        const hasKeywords = Object.values(kwMap).some((v) => Array.isArray(v) && v.length > 0);
        if (!hasKeywords) {
          kwMap = await ensureAllChartKeywords(guidanceConfig.charts);
          activeKeywordMap = kwMap;
        }
        // A locked chart also confines the keyword hunt to that chart's bundles —
        // searching every chart would pull the reading back to the chip's wording.
        let kwInference = lockedChart
          ? { chart: lockedChart, keyword: findKeywordInText(matchText, kwMap[lockedChart] || []) }
          : inferChartAndKeyword(matchText, guidanceConfig.charts, kwMap, selectedContextRef.current);
        // Some bundles carry non-English synonyms next to the English ones (e.g.
        // "4d, 买彩票, 中彩, 彩票"), so the untranslated question can match where the
        // translation does not.
        if (!kwInference.keyword && matchText !== text) {
          const originalInference = lockedChart
            ? { chart: lockedChart, keyword: findKeywordInText(text, kwMap[lockedChart] || []) }
            : inferChartAndKeyword(text, guidanceConfig.charts, kwMap, selectedContextRef.current);
          if (originalInference.keyword) kwInference = originalInference;
        }
        if (!inferredChart && kwInference.chart) inferredChart = kwInference.chart;
        inferredKeyword = kwInference.keyword;
      } else {
        ({ chart: inferredChart, keyword: inferredKeyword } = inferChartAndKeyword(
          matchText,
          guidanceConfig.charts,
          activeKeywordMap,
          selectedContextRef.current
        ));

        if (!inferredChart) {
          activeKeywordMap = await ensureAllChartKeywords(guidanceConfig.charts);
          const refreshedInference = inferChartAndKeyword(
            matchText,
            guidanceConfig.charts,
            activeKeywordMap,
            selectedContextRef.current
          );
          inferredChart = refreshedInference.chart;
          inferredKeyword = refreshedInference.keyword;
        }
      }

      // console.log("Resolved period (pre-AI):", normalizedPeriod, "explicit:", periodIsExplicit);
      // console.log("Resolved chart hint (pre-AI):", inferredChart);
      // console.log("Resolved keyword hint (pre-AI):", inferredKeyword);

      if (!inferredChart) {
        if (directMode) {
          // console.log("No explicit chart detected. Letting OpenRouter infer chart.");
        } else {
          inferredChart = selectedContextRef.current.chart || guidanceConfig.charts?.[0] || "wisdom";
          // console.log("No explicit chart detected. Defaulting chart to:", inferredChart);
        }
      }

      const categorySelectionInput = isCategorySelectionInput(matchText, normalizedPeriod, inferredChart);
      const activePeriod = normalizedPeriod;

      selectedContextRef.current = {
        period: activePeriod,
        chart: inferredChart || selectedContextRef.current.chart,
      };

      let chartKeywordMap = activeKeywordMap;
      if (!directMode && inferredChart && (!chartKeywordMap[inferredChart] || chartKeywordMap[inferredChart].length === 0)) {
        chartKeywordMap = await ensureChartKeywords(inferredChart);
      }

      const normalizedDisplayText = normalizeForMatch(displayText || "");
      const normalizedQueryText = normalizeForMatch(matchText);
      const clickedKeywordMatch = !directMode && normalizedDisplayText !== normalizedQueryText
        ? findKeywordMatch(displayText, chartKeywordMap[inferredChart] || [])
        : null;
      const clickedKeywordOverride = clickedKeywordMatch?.bundle || null;
      // inferredKeyword may have matched against `text` instead of `matchText` (the
      // non-English-synonym fallback above), so re-score against whichever one hit.
      const inferredKeywordMatch = !clickedKeywordMatch && inferredKeyword
        ? (findKeywordMatch(matchText, chartKeywordMap[inferredChart] || [])
          || findKeywordMatch(text, chartKeywordMap[inferredChart] || []))
        : null;
      const chartKeywordScore = clickedKeywordMatch?.score ?? inferredKeywordMatch?.score ?? -Infinity;
      let effectiveKeyword = clickedKeywordOverride || inferredKeyword;

      // "General" keywords (wealth colour, favourable element, etc.) are a fixed
      // Gender/DayMaster/Strength lookup with no period or chart of their own, matched
      // independently from the per-chart catalog above.
      const generalKeywordMatch = findKeywordMatch(matchText, generalKeywordsRef.current)
        || (matchText !== text ? findKeywordMatch(text, generalKeywordsRef.current) : null);
      let hasRealChartKeyword = chartKeywordScore > VAGUE_KEYWORD_SCORE;
      // A real per-chart hit ("wealth") and a general-catalog hit ("improve wealth", "property")
      // can each name a genuine part of the same question ("how do I improve wealth?", "can I
      // invest in property?") — keep both bundles so their guidance seeds get combined below
      // instead of one silently replacing the other. With no real chart hit, the more specific
      // general match still wins alone, exactly as before.
      let generalKeywordBundle = generalKeywordMatch && (hasRealChartKeyword || generalKeywordMatch.score > chartKeywordScore)
        ? generalKeywordMatch.bundle
        : null;
      let combineWithGeneral = hasRealChartKeyword && !!generalKeywordBundle;
      if (generalKeywordBundle) {
        console.log(
          "[useFairyChat] Resolved general keyword hint (pre-AI):",
          generalKeywordBundle,
          combineWithGeneral ? "(combining with chart keyword)" : ""
        );
      }

      // Nothing hit literally, and no *real* per-chart keyword matched either (a strong
      // per-chart hit already means this isn't a general-bucket question) — worth the extra
      // AI round trip here specifically, since the alternative is buildGeneralFallbackGuidance's
      // generic non-answer. A per-chart hit that only cleared VAGUE_KEYWORDS ("should i" inside
      // "what style should i get") doesn't count as a real hit here — it was scored near-zero
      // precisely so it wouldn't out-rank a topical keyword, and letting it block this search
      // entirely defeats that: it silently pins the answer to whichever chart the vague phrase
      // happens to live in (wisdom) instead of ever giving general a chance to catch the actual
      // topic ("hairstyle").
      // Both AI lookups run together when there's no real local hit yet: the chart-side one
      // catches a topical question phrased in different words than the catalog (this message's
      // "kids" vs the relationship catalog's "children"), the general-side one catches the same
      // gap for the fixed gender/day-master/strength bucket. `inferredChart` at this point may
      // just be carried over from the last unrelated turn (inferChartAndKeyword falls back to
      // selectedContext.chart when nothing matches), so a genuine AI hit on a different chart
      // is trusted over it rather than the other way around.
      if (!hasRealChartKeyword && directMode) {
        const openRouterConfig = getOpenRouterConfig();
        const referer = typeof window !== "undefined" ? window.location.origin : "";
        const chartSearchList = lockedChart ? [lockedChart] : guidanceConfig.charts;

        const [aiChartMatch, aiGeneralMatch] = await Promise.all([
          resolveChartKeywordWithOpenRouter({
            openRouterConfig,
            userText: matchText,
            keywordsByChart: keywordsByChartRef.current,
            chartList: chartSearchList,
            referer,
          }),
          (!generalKeywordBundle && generalKeywordsRef.current.length > 0)
            ? resolveGeneralKeywordWithOpenRouter({
              openRouterConfig,
              userText: matchText,
              generalKeywords: generalKeywordsRef.current,
              referer,
            })
            : Promise.resolve(null),
        ]);

        if (aiChartMatch) {
          effectiveKeyword = aiChartMatch.keyword;
          hasRealChartKeyword = true;
          if (!lockedChart && inferredChart !== aiChartMatch.chart) {
            inferredChart = aiChartMatch.chart;
            selectedContextRef.current = { period: activePeriod, chart: inferredChart };
          }
          console.log(
            "[useFairyChat] Resolved chart keyword hint (AI):",
            aiChartMatch.keyword,
            "chart:",
            aiChartMatch.chart
          );
        } else {
          console.log("[useFairyChat] AI chart keyword match: no catalog entry judged relevant.");
        }

        if (aiGeneralMatch) {
          generalKeywordBundle = aiGeneralMatch;
          console.log("[useFairyChat] Resolved general keyword hint (AI):", generalKeywordBundle);
        }
      }

      combineWithGeneral = hasRealChartKeyword && !!generalKeywordBundle;
      if (generalKeywordBundle && !combineWithGeneral) {
        effectiveKeyword = null;
      }

        const requestGuidanceAnswer = async (targetPeriod, targetKeyword) => {
          // Use cached profile immediately to keep chat latency low.
          const currentProfile = userProfileRef.current;

          const chartValue = getChartValueByPeriod(currentProfile.chartValues, inferredChart, targetPeriod);
          const percentBracket = toPercentBracket(chartValue);

          if (shouldUseOpenRouterDirect()) {
            const openRouterConfig = getOpenRouterConfig();
            const periodScores = inferredChart
              ? getChartSnapshotByPeriod(currentProfile.chartValues, inferredChart)
              : {};
            const referer = typeof window !== "undefined" ? window.location.origin : "";

            const aiResolvedPeriod = periodIsExplicit
              ? targetPeriod
              : await resolvePeriodWithOpenRouter({
                openRouterConfig,
                userText: matchText,
                fallbackPeriod: targetPeriod,
                allowedPeriods: guidanceConfig.periods,
                referer,
              });

            console.log(
              "[useFairyChat] OpenRouter resolved period:",
              aiResolvedPeriod,
              periodIsExplicit ? "(explicit period, classifier skipped)" : "(classified)"
            );

            // Fetches one guidance-DB seed answer and returns its trimmed text, or null on any
            // failure/empty result (200-on-failure included — see the ModelStateDictionary note
            // above fetchGreetingTip).
            const fetchGuidanceSeedText = async ({ category, keyword, seedPercentBracket }) => {
              try {
                const seedPayload = {
                  category,
                  keyword,
                  genderDesc: (currentProfile.gender || "unknown").toLowerCase(),
                  symbol: currentProfile.lunarData?.A2 || "",
                  strengthDesc: currentProfile.strengthCategory || "unknown",
                  percentBracket: seedPercentBracket,
                };
                console.log("[useFairyChat] Seed payload prepared:", seedPayload, JSON.stringify(seedPayload));
                console.log("[useFairyChat] Fetching guidance seed for keyword:", keyword, "payload:", seedPayload);
                const seedRes = await fetch(GET_GUIDANCE_ANSWER_URL, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify(seedPayload),
                });
                if (!seedRes.ok) return null;
                const seedData = await seedRes.json();
                console.log("[useFairyChat] Guidance seed response:", seedData);
                const rawSeed = seedData?.AnswerText || seedData?.answerText || seedData?.answer || "";
                return rawSeed.trim() || null;
              } catch {
                return null;
              }
            };

            // When a keyword is matched, fetch a seed answer from the guidance API to enrich with AI.
            // A general-bucket match (no period/chart of its own) takes the literal "General"
            // category with a placeholder bracket — the answer is identical across brackets there,
            // so the real chart+period lookup only runs when no general keyword matched.
            // A question can name both a chart-specific topic ("investment") and an unrelated
            // general-catalog one ("property") at once (combineWithGeneral) — fetch both seeds
            // and combine them so the AI call below weaves both into one integrated answer
            // instead of one silently winning over the other.
            let seedAnswer = null;
            let seedKeyword = targetKeyword || generalKeywordBundle || null;
            if (targetKeyword && generalKeywordBundle && combineWithGeneral) {
              seedKeyword = `${targetKeyword}, ${generalKeywordBundle}`;
              const [chartSeed, generalSeed] = await Promise.all([
                fetchGuidanceSeedText({
                  category: buildCategoryPrompt(aiResolvedPeriod, chartApiMapRef.current[inferredChart] || inferredChart),
                  keyword: targetKeyword,
                  seedPercentBracket: percentBracket || "0,20",
                }),
                fetchGuidanceSeedText({
                  category: "General",
                  keyword: generalKeywordBundle,
                  seedPercentBracket: "0,20",
                }),
              ]);
              seedAnswer = [chartSeed, generalSeed].filter(Boolean).join("\n\n") || null;
              if (seedAnswer) {
                console.log("[useFairyChat] Combined guidance seeds (chart + general):", seedAnswer);
              }
            } else {
              const isGeneralSeed = !targetKeyword && !!generalKeywordBundle;
              if (seedKeyword) {
                seedAnswer = await fetchGuidanceSeedText({
                  category: isGeneralSeed
                    ? "General"
                    : buildCategoryPrompt(aiResolvedPeriod, chartApiMapRef.current[inferredChart] || inferredChart),
                  keyword: seedKeyword,
                  seedPercentBracket: isGeneralSeed ? "0,20" : (percentBracket || "0,20"),
                });
                if (seedAnswer) {
                  console.log("[useFairyChat] Guidance seed answer obtained:", seedAnswer);
                }
              }
            }

            // "low" reasoning (not "none") so the model gets a self-check pass against the
            // jargon-ban and no-period rules before finalizing. That's cheaper than leaning on
            // requestGuidanceCompletion's leak retry, which costs a whole second round trip.
            const parsedGuidance = await requestGuidanceCompletion({
              openRouterConfig,
              messages: buildOpenRouterMessages({
                userText: displayText || text,
                period: aiResolvedPeriod,
                availablePeriods: guidanceConfig.periods,
                availableCharts: guidanceConfig.charts,
                chart: inferredChart,
                keyword: seedKeyword,
                profile: currentProfile,
                periodScores,
                seedAnswer,
                language: uiLanguage,
                periodIsExplicit,
                history: aiHistory,
              }),
              temperature: 0.4,
              referer,
              fallbackPeriod: aiResolvedPeriod,
              allowedPeriods: guidanceConfig.periods,
              allowedCharts: guidanceConfig.charts,
              fallbackChart: inferredChart || selectedContextRef.current.chart || null,
              periodIsExplicit,
            });
            const resolvedPeriod = periodIsExplicit
              ? aiResolvedPeriod
              : (parsedGuidance.period || aiResolvedPeriod);
            const resolvedCharts = Array.isArray(parsedGuidance.charts) ? parsedGuidance.charts : [];
            // The chip's lock outranks the classifier too — otherwise the model reads
            // "Health sciences" and hands the reading to the health chart anyway.
            const resolvedChart = lockedChart || resolvedCharts[0] || inferredChart || selectedContextRef.current.chart || null;
            const resolvedKeyword = parsedGuidance.keyword || null;

            // console.log("[useFairyChat] Resolved period (AI):", resolvedPeriod);
            // console.log("[useFairyChat] Resolved keyword (AI):", resolvedKeyword);
            // console.log("[useFairyChat] Resolved primary chart (AI):", resolvedChart);
            if (resolvedCharts.length > 1) {
              // console.log("[useFairyChat] OpenRouter multi-chart match:", resolvedCharts);
            }

            selectedContextRef.current = {
              period: resolvedPeriod,
              chart: resolvedChart || selectedContextRef.current.chart,
            };

            const mainAnswer = parsedGuidance.answer || "I could not generate guidance right now. Please try again.";

            // If no pre-AI seed was used, try guidance DB with AI-detected keyword for post-enrichment.
            if (!seedAnswer && resolvedKeyword) {
              try {
                // The AI returns a single synonym; the API only matches the stored bundle.
                // Chart-specific bundles are checked first (a real topical hit should win),
                // and only when none of those match is the "general" bucket tried — it lives
                // outside keywordsByChartRef entirely, so without this second lookup a
                // general-only keyword (e.g. "suitable hairstyle, haircut") could never be
                // found here and the answer would stay pinned to whatever chart the classifier
                // guessed.
                const keywordMatch = resolveKeywordBundle(
                  resolvedKeyword,
                  keywordsByChartRef.current,
                  // Locked chart first so the bundle is looked up in the chart the chip
                  // belongs to, not the one the classifier read out of its label.
                  lockedChart
                    ? [lockedChart, ...resolvedCharts, resolvedChart, inferredChart]
                    : [...resolvedCharts, resolvedChart, inferredChart]
                );
                const generalKeywordMatch = !keywordMatch
                  ? resolveKeywordBundle(resolvedKeyword, { general: generalKeywordsRef.current })
                  : null;
                const isGeneralSeed = !!generalKeywordMatch;
                const seedKeyword = keywordMatch?.bundle || generalKeywordMatch?.bundle || resolvedKeyword;
                const seedChart = keywordMatch?.chart || resolvedChart || inferredChart || "wisdom";
                const postSeedPayload = {
                  category: isGeneralSeed
                    ? "General"
                    : buildCategoryPrompt(resolvedPeriod, chartApiMapRef.current[seedChart] || seedChart),
                  keyword: seedKeyword,
                  genderDesc: (currentProfile.gender || "unknown").toLowerCase(),
                  symbol: currentProfile.lunarData?.A2 || "",
                  strengthDesc: currentProfile.strengthCategory || "unknown",
                  percentBracket: isGeneralSeed ? "0,20" : (percentBracket || "0,20"),
                };
                console.log("[useFairyChat] Post-AI guidance seed fetch for keyword:", resolvedKeyword);
                if (keywordMatch) {
                  console.log("[useFairyChat] Post-AI keyword resolved to bundle:", seedKeyword, "chart:", seedChart);
                } else if (generalKeywordMatch) {
                  console.log("[useFairyChat] Post-AI keyword resolved to general bundle:", seedKeyword);
                } else {
                  console.warn("[useFairyChat] Post-AI keyword had no bundle match. Sending as-is:", resolvedKeyword);
                }
                console.log("[useFairyChat] Post-AI guidance seed payload:", postSeedPayload);
                console.log("[useFairyChat] Post-AI guidance seed POST URL:", GET_GUIDANCE_ANSWER_URL);
                const postSeedRes = await fetch(GET_GUIDANCE_ANSWER_URL, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify(postSeedPayload),
                });
                // console.log("[useFairyChat] Post-AI guidance seed status:", postSeedRes.status);
                if (!postSeedRes.ok) {
                  console.warn(
                    "[useFairyChat] Post-AI guidance seed lookup failed:",
                    postSeedRes.status,
                    postSeedRes.status === 404 ? "(no row matched category + keyword)" : ""
                  );
                }
                if (postSeedRes.ok) {
                  const postSeedData = await postSeedRes.json();
                  console.log("[useFairyChat] Post-AI guidance seed response:", postSeedData);
                  const postRawSeed = postSeedData?.AnswerText || postSeedData?.answerText || postSeedData?.answer || "";
                  if (postRawSeed.trim()) {
                    console.log("[useFairyChat] Post-AI guidance seed obtained. Making enrichment call.");
                    const enrichedGuidance = await requestGuidanceCompletion({
                      openRouterConfig,
                      messages: buildOpenRouterMessages({
                        userText: displayText || text,
                        period: resolvedPeriod,
                        availablePeriods: guidanceConfig.periods,
                        availableCharts: guidanceConfig.charts,
                        chart: resolvedChart || inferredChart,
                        keyword: resolvedKeyword,
                        profile: currentProfile,
                        periodScores,
                        seedAnswer: postRawSeed.trim(),
                        language: uiLanguage,
                        periodIsExplicit,
                        history: aiHistory,
                      }),
                      temperature: 0.4,
                      referer,
                      fallbackPeriod: resolvedPeriod,
                      allowedPeriods: guidanceConfig.periods,
                      allowedCharts: guidanceConfig.charts,
                      fallbackChart: resolvedChart,
                      periodIsExplicit,
                    });
                    if (enrichedGuidance.answer) {
                      const enrichedPeriod = periodIsExplicit
                        ? resolvedPeriod
                        : (enrichedGuidance.period || resolvedPeriod);
                      selectedContextRef.current = {
                        period: enrichedPeriod,
                        chart: enrichedGuidance.charts?.[0] || resolvedChart || selectedContextRef.current.chart,
                      };
                      const enrichedReply = enrichedGuidance.followUp
                        ? `${enrichedGuidance.answer}\n\n${enrichedGuidance.followUp}`
                        : enrichedGuidance.answer;
                      appendBotReply(enrichedReply);
                      return {
                        ok: true,
                        notFound: false,
                        blocked: false,
                        period: enrichedPeriod,
                        chart: enrichedGuidance.charts?.[0] || resolvedChart,
                        charts: enrichedGuidance.charts || resolvedCharts,
                        keyword: enrichedGuidance.keyword || resolvedKeyword,
                        aiSuggestions: enrichedGuidance.suggestions || parsedGuidance.suggestions || [],
                      };
                    }
                  }
                }
              } catch (postSeedError) {
                // Fall back to initial AI answer below.
                console.warn("[useFairyChat] Post-AI guidance seed step errored:", postSeedError);
              }
            }

            const fullReply = parsedGuidance.followUp
              ? `${mainAnswer}\n\n${parsedGuidance.followUp}`
              : mainAnswer;
            appendBotReply(fullReply);
            return {
              ok: true,
              notFound: false,
              blocked: false,
              period: resolvedPeriod,
              chart: resolvedChart,
              charts: resolvedCharts,
              keyword: resolvedKeyword,
              aiSuggestions: parsedGuidance.suggestions || [],
            };
          }

          const payload = {
            category: buildCategoryPrompt(targetPeriod, chartApiMapRef.current[inferredChart] || inferredChart),
            keyword: targetKeyword,
            genderDesc: (currentProfile.gender || "unknown").toLowerCase(),
            symbol: currentProfile.lunarData?.A2 || "",
            strengthDesc: currentProfile.strengthCategory || "unknown",
            percentBracket: percentBracket || "0,20",
          };

        // console.log("[useFairyChat] Guidance answer category:", payload.category);
        // console.log("[useFairyChat] Guidance answer keyword:", payload.keyword);
        // console.log("[useFairyChat] Guidance answer payload:", payload);
        // console.log("[useFairyChat] POST URL:", GET_GUIDANCE_ANSWER_URL);

        const answerRes = await fetch(GET_GUIDANCE_ANSWER_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });

        // console.log("[useFairyChat] Guidance answer status:", answerRes.status);

        if (answerRes.status === 404) {
          console.warn("[useFairyChat] No answer row matched payload (404).");
          const fallbackGuidance = buildGeneralFallbackGuidance({
            period: targetPeriod,
            chart: inferredChart,
            keyword: targetKeyword,
            chartValue,
            profile: currentProfile,
          });
          appendBotReply(fallbackGuidance);
          return { ok: true, notFound: false, blocked: false, fallback: true };
        }

        if (!answerRes.ok) {
          // Some error responses still carry a usable answer in the body (e.g. a
          // non-2xx status alongside AnswerText). Use it instead of failing outright.
          let errorBodyAnswer = "";
          try {
            const errorData = await answerRes.json();
            errorBodyAnswer = String(
              errorData?.originalAnswer || errorData?.AnswerText || errorData?.answerText || errorData?.answer || ""
            ).trim();
          } catch {
            // Body was not JSON or was empty; fall through to throwing below.
          }

          if (errorBodyAnswer) {
            console.warn(
              "[useFairyChat] Guidance answer request failed but body had AnswerText; using it:",
              answerRes.status
            );
            appendBotReply(errorBodyAnswer);
            return { ok: true, notFound: false, blocked: false };
          }

          throw new Error(`Guidance answer failed: ${answerRes.status}`);
        }

        const answerData = await answerRes.json();
        // console.log("[useFairyChat] Guidance answer response:", answerData);
        const originalAnswer = answerData?.originalAnswer || answerData?.AnswerText || answerData?.answerText || answerData?.answer || "";
        const expanded = answerData?.expandedGuidance || answerData?.expandedguidance || "";
        // Prefer showing expanded guidance when available, but include original as a short header when present.
        let messageText = "";
        if (expanded && expanded.trim()) {
          if (originalAnswer && originalAnswer.trim()) {
            // messageText = `${originalAnswer}\n\n${expanded}`;
            messageText = `${expanded}`;
          } else {
            messageText = expanded;
          }
        } else {
          messageText = originalAnswer || `I could not find an answer for ${targetKeyword}.`;
        }

        appendBotReply(messageText);

        return { ok: true, notFound: false, blocked: false };
      };

      // console.log("Category selection input:", categorySelectionInput);
      // console.log("Clicked keyword override:", clickedKeywordOverride);
      // console.log("Effective keyword:", effectiveKeyword);

      if (shouldUseOpenRouterDirect()) {
        const directAnswerResult = await requestGuidanceAnswer(activePeriod, effectiveKeyword || null);
        const resolvedPeriod = directAnswerResult?.period || activePeriod;
        const resolvedChart = directAnswerResult?.chart || inferredChart;
        const aiSuggestions = directAnswerResult?.aiSuggestions || [];
        if (aiSuggestions.length > 0) {
          // Carry the answering chart on each chip so clicking one continues this
          // reading instead of being re-classified from the chip's own wording.
          setSuggestions(
            aiSuggestions.map((text) => ({
              id: null,
              text,
              contextChart: resolvedChart || inferredChart || selectedContextRef.current.chart || null,
            }))
          );
        }
        if (userMessageCountRef.current >= SHOP_SUGGEST_AFTER_EXCHANGE) {
          const chartForShop = resolvedChart || inferredChart || selectedContextRef.current.chart;
          const periodForShop = resolvedPeriod || activePeriod;
          if (chartForShop) {
            fetchShopSuggestionsForChart(chartForShop, periodForShop).then((items) => {
              if (items.length > 0) setShopSuggestions(items);
            });
          }
        }
        if (userMessageCountRef.current >= CONSULTATION_SUGGEST_AFTER_EXCHANGE) {
          const chartForConsultation = resolvedChart || inferredChart || selectedContextRef.current.chart;
          fetchConsultationSuggestion(chartForConsultation, displayText || text, effectiveKeyword).then((masters) => {
            if (masters && masters.length > 0) setConsultationSuggestions(masters);
          });
        }
        return;
      }

      if (isInitialInteraction && categorySelectionInput) {
        const initialKeyword = pickInitialKeywordForChart(inferredChart, chartKeywordMap);
        // console.log("[useFairyChat] Initial category flow period:", activePeriod);
        // console.log("[useFairyChat] Initial category flow keyword:", initialKeyword);

        if (!initialKeyword) {
          appendKeywordSuggestions(activePeriod, inferredChart, chartKeywordMap);
          return;
        }

        const initialAnswerResult = await requestGuidanceAnswer(activePeriod, initialKeyword);
        if (initialAnswerResult.notFound && !initialAnswerResult.blocked) {
          appendKeywordSuggestions(activePeriod, inferredChart, chartKeywordMap);
        }
        return;
      }

      const fallbackKeyword = effectiveKeyword || pickInitialKeywordForChart(inferredChart, chartKeywordMap) || null;
      if (categorySelectionInput || !effectiveKeyword) {
        // console.log("[useFairyChat] No exact keyword detected. Proceeding with fallback answer flow.");
      }

      const answerResult = await requestGuidanceAnswer(activePeriod, fallbackKeyword);
      if (answerResult.notFound) {
        appendKeywordSuggestions(activePeriod, inferredChart, chartKeywordMap);
        return;
      }
    } catch (error) {
      console.error("[useFairyChat] sendMessage failed:", error);
      appendBotReply(i18n.t("chat.guidanceUnreachable"));
    } finally {
      console.groupEnd();
      setLoading(false);
    }
  };

  const clearMessages = () => {
    setMessages([]);
    setSuggestions([]);
    setShopSuggestions([]);
    setConsultationSuggestions([]);
    setLoading(false);
    userMessageCountRef.current = 0;
    selectedContextRef.current = {
      period: guidanceConfig.defaultPeriod || "monthly",
      chart: null,
    };

    // Reset lands the thread back on an empty state, so bring the greeting back with it.
    greetedRef.current = true;
    showOpeningGreeting();
  };

  return {
    messages,
    loading,
    suggestions,
    shopSuggestions,
    consultationSuggestions,
    chatProducts,
    purchaseChatProduct,
    chatCredit,
    outOfCredit,
    refreshChatCredit,
    keywords,
    userProfile,
    sendMessage,
    selectCategory,
    clearMessages,
    resetChat: clearMessages,
  };
}
